/**
 * /attendance-tracker — страница журнала посещаемости (05.11.2026).
 *
 * Ядро — core.js (данные без DOM), документы — forms.js. Готовые модули сайта:
 *   сетка месяца — timesheet/grid.js (имена колонкой, занятия в прокрутке, кисть, диапазон);
 *   импорт списка — staff-roster/ (вставка, CSV в любой кодировке, Excel);
 *   календарь страны — printable-calendar/holiday-source.js (РФ — производственный календарь и
 *   праздники субъектов, остальные страны — слой праздников по актам);
 *   расписание — calendar/rrule.js (через core.js); время и дни недели по обычаю языка —
 *   school-timetable/locale.js; выдача — HtmlPdf, XlsxWriter, __downloadFile; лимит — __toolLimit.
 * Журналы живут на устройстве (__toolDraft: «Мои журналы»). В телеметрии — только счётчики:
 * ни имён, ни дат рождения, ни телефонов, ни сумм.
 * Облако (срез 2, Премиум) — cloud-ui.js поверх cloud.js: на сервер уходит журнал без
 * персональных данных, имена переходят «ключом имён». Выписка по участнику — statement.js.
 */
import * as A from './core.js';
import * as F from './forms.js';
import { KEY_KIND } from './cloud.js';
import { createCloud } from './cloud-ui.js';
import { statementPdf } from './statement.js';
import { createGrid } from '../timesheet/grid.js';
import { importText as rosterText, templateSheet } from '../staff-roster/roster.js';
import { readRosterFile, kindOf as rosterKind } from '../staff-roster/read.js';
import { decodeBytes } from '../subtitle-convert/decode.js';
import * as HS from '../printable-calendar/holiday-source.js';
import { fmtTime, dayNames, conv } from '../school-timetable/locale.js';

const SLUG = 'attendance-tracker';
const CFG = window.__AT || {};
const I = CFG.i18n || {};
const UI = I.ui || {};
const LANG = CFG.locale || 'en';
const RU = !!CFG.ru;
const $ = (id) => document.getElementById(id);
const LOC = () => (window.__intlLocale ? window.__intlLocale() : LANG);

/* ── мелочи ───────────────────────────────────────────────────────────── */
function t(k, r) { const s = UI[k] != null ? String(UI[k]) : k; return r ? s.replace(/:(\w+)/g, (m, x) => (r[x] != null ? r[x] : m)) : s; }
/** Число с формой слова: «2 занятия» — формы через «|» по правилу языка (Intl.PluralRules). */
function tn(k, n, r) {
    const forms = String(UI[k] != null ? UI[k] : k).split('|');
    let i = 0;
    if (forms.length > 1) {
        let cat = 'other';
        try { cat = new Intl.PluralRules(LANG).select(n); } catch (_) {}
        const order = { one: 0, few: 1, many: 2, other: forms.length - 1 };
        i = forms.length === 2 ? (cat === 'one' ? 0 : 1) : Math.min(forms.length - 1, order[cat] != null ? order[cat] : forms.length - 1);
    }
    return forms[i].replace(/:n\b/g, String(n)).replace(/:(\w+)/g, (m, x) => (r && r[x] != null ? r[x] : m));
}
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) {} }
function fu(action, data) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action }, data || {})); } catch (_) {} }
function stage(name, meta) { try { if (window.__stage) window.__stage(name, meta || {}); } catch (_) {} }
function diag(issue, data) { try { if (window.__aeDiagnostic) window.__aeDiagnostic(Object.assign({ issue }, data || {})); } catch (_) {} }
const bucket = A.bucket;
function todayIso() { try { if (window.__localYmd) return window.__localYmd(); } catch (_) {} return CFG.today || A.isoOf(Math.floor(Date.now() / 864e5)); }
function cssEsc(s) { return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'); }
function saveSet(k, v) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (_) {} }
function loadSet(k, d) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (_) { return d; } }

/* ── форматы дат, времени и денег на языке страницы ───────────────────── */
const fmtCache = {};
function dtf(o) { const k = JSON.stringify(o); if (!fmtCache[k]) { try { fmtCache[k] = new Intl.DateTimeFormat(LOC(), Object.assign({ timeZone: 'UTC' }, o)); } catch (_) { fmtCache[k] = new Intl.DateTimeFormat('en', Object.assign({ timeZone: 'UTC' }, o)); } } return fmtCache[k]; }
const utc = (iso) => new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10), 12));
const cap = (s) => (s ? s.charAt(0).toLocaleUpperCase(LOC()) + s.slice(1) : s);
const fmt = {
    day: (iso) => String(+iso.slice(8, 10)),
    dow: (iso) => dtf({ weekday: 'short' }).format(utc(iso)).replace('.', ''),
    date: (iso) => dtf({ day: 'numeric', month: 'numeric', year: 'numeric' }).format(utc(iso)),
    dm: (iso) => dtf({ day: 'numeric', month: 'long' }).format(utc(iso)),
    full: (iso) => cap(dtf({ weekday: 'long', day: 'numeric', month: 'long' }).format(utc(iso))),
    month: (y, m) => cap(dtf({ month: 'long', year: 'numeric' }).format(new Date(Date.UTC(y, m - 1, 15)))),
    time: (hm) => { const m = /^(\d{1,2}):(\d{2})$/.exec(hm || ''); return m ? fmtTime(+m[1] * 60 + +m[2], LANG) : ''; },
    money: (n) => {
        const cur = J && J.money && J.money.cur;
        try { if (cur) return new Intl.NumberFormat(LOC(), { style: 'currency', currency: cur, maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(n); } catch (_) {}
        try { return new Intl.NumberFormat(LOC(), { maximumFractionDigits: 2 }).format(n); } catch (_) { return String(n); }
    },
    num: (n) => { try { return new Intl.NumberFormat(LOC(), { maximumFractionDigits: 1 }).format(n); } catch (_) { return String(n); } },
};
/** Короткая подпись занятия: время или название переклички. */
function sessLabel(s) { return s.t ? fmt.time(s.t) : (s.label || ''); }

/* ── календарь страны: holiday-source (РФ + субъекты, прочие страны) ───── */
const calYears = new Map();
let calReady = false;
function calYear(y) {
    const cc = (J && J.cc) || 'NONE', reg = (J && J.region) || '';
    const k = cc + '|' + reg + '|' + y;
    if (calYears.has(k)) return calYears.get(k);
    let Y = null;
    try { Y = HS.yearDays({ country: cc, region: reg, year: y, rc: window.RuWorkCalendar }); } catch (_) { Y = null; }
    // таблица страны ещё грузится — не запоминать «нет данных»
    if (Y && !(Y.notes || []).includes('data_loading') && !(cc === 'RU' && !window.RuWorkCalendar)) calYears.set(k, Y);
    return Y;
}
const cal = {
    workday(iso) {
        const Y = calYear(+iso.slice(0, 4));
        if (!Y || (Y.status === 'none' && (Y.notes || []).includes('data_loading'))) return null;
        const d = Y.days.get(iso);
        if (d && d.kind === 'worksat') return true;
        if (d && d.off) return false;
        const wk = (Y.workWeek && Y.workWeek.weekend) || [6, 0];
        return wk.indexOf(utc(iso).getUTCDay()) < 0;
    },
    holiday(iso) {
        const Y = calYear(+iso.slice(0, 4));
        const d = Y && Y.days.get(iso);
        return d && d.off ? (d.names.filter(Boolean).join(', ') || t('holiday')) : '';
    },
};
function prepareCal() {
    const cc = (J && J.cc) || HS.defaultCountry(LANG);
    return HS.prepare(cc).then(() => { calYears.clear(); calReady = true; if (J) paintAll(); });
}

/* ── состояние ────────────────────────────────────────────────────────── */
let J = null;
let drafts = null;
let grid = null;
let cloud = null;
const hist = { past: [], future: [] };
const ui = {
    view: 'roll', date: todayIso(), sk: '', month: [+todayIso().slice(0, 4), +todayIso().slice(5, 7)],
    brush: '', range: false, open: '', openKind: '', period: 'month', pFrom: '', pTo: '', pay: '', filter: 'all',
    pOpen: '', search: '', setOpen: {},
};
let started = false;

/**
 * Снимок для «Отменить» — журнал без облачного состояния (J.cloud: ревизия, что уже на сервере,
 * что ждёт отправки). «Отменить» возвращает правку человека, а не синхронизацию: облако остаётся
 * текущим, и возвращённая клетка уходит на сервер обычной правкой.
 */
function snap() {
    if (!J) return '';
    const c = J.cloud;
    if (!c) return JSON.stringify(J);
    delete J.cloud;
    const s = JSON.stringify(J);
    J.cloud = c;
    return s;
}
function restore(s) {
    const c = J && J.cloud;
    const n = A.sanitize(JSON.parse(s));
    if (c && c.uid === n.id) n.cloud = c;
    return n;
}
function push(before) {
    hist.past.push(before != null ? before : snap());
    if (hist.past.length > 40) hist.past.shift();
    hist.future.length = 0;
    paintHist();
}
function undo() { if (!hist.past.length || !J) return; hist.future.push(snap()); J = restore(hist.past.pop()); fu('undo'); paintAll(); touch(); }
function redo() { if (!hist.future.length || !J) return; hist.past.push(snap()); J = restore(hist.future.pop()); fu('redo'); paintAll(); touch(); }
function paintHist() { const u = $('at-undo'), r = $('at-redo'); if (u) u.disabled = !hist.past.length; if (r) r.disabled = !hist.future.length; }
function clearHist() { hist.past.length = 0; hist.future.length = 0; paintHist(); }
function touch() {
    started = true;
    if (drafts) { try { drafts.touch(true); } catch (_) {} }
    try { if (J && J.members.length) localStorage.setItem('at_has_journal', '1'); } catch (_) {}
    if (cloud) cloud.changed();
}
/** Изменение журнала одной функцией: снимок для «Отменить», правка, сохранение, перерисовка. */
function change(fn, paint) {
    if (!J) return;
    const before = snap();
    fn();
    if (snap() !== before) { push(before); touch(); }
    (paint || paintAll)();
}

const today = () => todayIso();
const nameOf = (mid) => A.nameOf(J, mid) || t('noname');
function initials(name) {
    const w = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!w.length) return '?';
    return (w[0].charAt(0) + (w[1] ? w[1].charAt(0) : '')).toLocaleUpperCase(LOC());
}
const AV = ['#2563eb', '#db2777', '#059669', '#d97706', '#7c3aed', '#0891b2', '#dc2626', '#65a30d', '#c026d3', '#0d9488', '#ea580c', '#4f46e5'];
function presetName(p) { return (I.presets && I.presets[p] && I.presets[p].name) || p; }
function codeLabels() { return { codes: I.codes || {}, calls: I.calls || {} }; }
function defaultCurrency() { return ({ ru: 'RUB', uk: 'UAH', en: 'USD', de: 'EUR', fr: 'EUR', es: 'EUR', pt: 'BRL', id: 'IDR', ko: 'KRW', ar: 'SAR' })[LANG] || ''; }
const CURRENCIES = ['RUB', 'UAH', 'KZT', 'BYN', 'UZS', 'USD', 'EUR', 'GBP', 'CHF', 'CAD', 'MXN', 'ARS', 'COP', 'BRL', 'IDR', 'KRW', 'SAR', 'AED', 'EGP', 'TRY', 'PLN'];

/* ── журнал: создание ─────────────────────────────────────────────────── */
function createJournal(preset) {
    if (J && drafts) { try { drafts.leave(); } catch (_) {} }
    const cc = HS.defaultCountry(LANG);
    J = A.newJournal(preset, codeLabels(), { today: today(), cc, currency: defaultCurrency() });
    J.region = HS.defaultRegion(cc) || '';
    hist.past.length = 0; hist.future.length = 0;
    ui.date = today(); ui.sk = ''; ui.open = ''; ui.brush = '';
    ui.view = 'people';
    started = true;
    calYears.clear();
    prepareCal();
    fu('preset', { p: preset });
    stage('preset', { p: preset });
    paintAll();
    touch();
    if (cloud) cloud.start();
    setTimeout(() => { const q = $('at-quick-in'); if (q) { q.focus({ preventScroll: true }); q.scrollIntoView({ block: 'nearest' }); } }, 30);
}

/* ── отрисовка: общая рамка ───────────────────────────────────────────── */
function setView(v, from) {
    if (!J) return;
    ui.view = v;
    saveSet('view', v);
    if (from) fu('view', { v });
    paintAll();
}
function paintFrame() {
    const has = !!J;
    $('at-start').hidden = has;
    $('at-wait').hidden = true;
    $('at-jt').hidden = !has;
    $('at-tabs-row').hidden = !has;
    $('at-hist').hidden = !has;
    $('at-out').hidden = !has;
    ['roll', 'month', 'sum', 'people', 'set'].forEach((v) => {
        const p = $('at-v-' + v), tb = $('at-tab-' + v);
        if (p) p.hidden = !has || ui.view !== v;
        if (tb) { tb.classList.toggle('on', ui.view === v); tb.setAttribute('aria-selected', String(ui.view === v)); tb.tabIndex = ui.view === v ? 0 : -1; }
    });
    if (!has) return;
    const ti = $('at-title');
    if (document.activeElement !== ti) ti.value = J.title || '';
    ti.placeholder = t('title_ph_' + J.preset) !== 'title_ph_' + J.preset ? t('title_ph_' + J.preset) : ti.getAttribute('placeholder');
    $('at-jt-ico').textContent = (A.PRESETS[J.preset] || {}).icon || '📋';
    const n = J.members.length;
    $('at-count').textContent = n ? String(n) : '';
    $('at-count2').textContent = n ? String(n) : '';
    document.querySelectorAll('#at [data-mname]').forEach((el) => { el.textContent = fmt.month(ui.month[0], ui.month[1]); });
    const f608 = $('at-out-608');
    if (f608) f608.hidden = false;
    paintHist();
    codesCss();
}
function codesCss() {
    const st = document.getElementById('at-codes-css');
    if (!st || !J) return;
    st.textContent = J.codes.map((c, i) => `.at .ts-c.k-${i}{background:${tint(c.color, c.kind === 'p' ? 0.16 : 0.26)}}.at .ts-c.k-${i} b{color:color-mix(in srgb,${c.color} 62%,var(--lg-text))}`).join('');
}
function tint(hex, a) {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return 'transparent';
    const n = parseInt(m[1], 16);
    return 'rgba(' + (n >> 16) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
}
function paintAll() {
    paintFrame();
    if (!J) return;
    if (ui.view === 'roll') paintRoll();
    else if (ui.view === 'month') paintMonth();
    else if (ui.view === 'sum') paintSum();
    else if (ui.view === 'people') paintPeople();
    else if (ui.view === 'set') paintSettings();
}

/* ── перекличка ───────────────────────────────────────────────────────── */
function daySessions(iso) { return A.sessions(J, cal, iso, iso); }
function curSession() {
    const list = daySessions(ui.date);
    let s = list.find((x) => x.key === ui.sk);
    if (!s) s = list.find((x) => !x.cancelled) || list[0] || null;
    ui.sk = s ? s.key : '';
    return { s, list };
}
function shiftDay(dir) {
    // к соседнему дню с занятием (до 60 дней), иначе — просто на день
    let d = ui.date;
    for (let i = 0; i < 60; i++) {
        d = A.addDays(d, dir);
        if (daySessions(d).length) { ui.date = d; ui.sk = ''; ui.open = ''; paintAll(); return; }
    }
    ui.date = A.addDays(ui.date, dir); ui.sk = ''; ui.open = ''; paintAll();
}
function paintRoll() {
    const td = today();
    const { s, list } = curSession();
    $('at-day-l').textContent = fmt.full(ui.date) + (ui.date === td ? ' · ' + t('today_l') : '');
    $('at-day-in').value = ui.date;
    $('at-today').hidden = ui.date === td;
    const seg = $('at-sess');
    seg.hidden = list.length < 2;
    seg.innerHTML = list.map((x) => `<button type="button" class="tc-seg-b${s && x.key === s.key ? ' on' : ''}" data-sk="${esc(x.key)}" aria-pressed="${s && x.key === s.key}">${esc(sessLabel(x) || t('session_n', { n: x.i + 1 }))}${x.cancelled ? ' · ' + esc(t('cancelled_short')) : ''}</button>`).join('');
    const ans = $('at-ans'), act = $('at-roll-act'), roll = $('at-roll'), tools = $('at-sess-tools');
    if (!J.members.length) {
        ans.innerHTML = `<p class="at-ans-v">${esc(t('roll_nobody'))}</p><p class="at-ans-s">${esc(t('roll_nobody_sub'))}</p>`;
        act.innerHTML = `<button type="button" class="tc-btn" data-go="people">${esc(t('go_people'))}</button>`;
        roll.innerHTML = ''; tools.innerHTML = '';
        return;
    }
    if (!s) {
        const hol = cal.holiday(ui.date);
        const why = hol ? t('no_session_hol', { name: hol }) : J.when.mode === 'events' ? t('no_session_events') : t('no_session_sub');
        ans.innerHTML = `<p class="at-ans-v">${esc(t('no_session'))}</p><p class="at-ans-s">${esc(why)}</p>`;
        act.innerHTML = `<button type="button" class="tc-btn" data-roll="add-session">${esc(J.when.mode === 'events' ? t('add_event') : t('add_session'))}</button>`
            + (J.when.mode === 'schedule' && !(J.when.slots || []).length ? `<button type="button" class="tc-btn ghost" data-go="set" data-open="when">${esc(t('set_schedule'))}</button>` : '');
        roll.innerHTML = '';
        tools.innerHTML = paintForecast(td);
        return;
    }
    const before = s.d <= td;
    const st = A.stats(J, cal, J.start || s.d, s.d, td);
    const ss = st.bySession[s.key] || { present: 0, absent: 0, unknown: 0, n: 0 };
    const members = A.activeMembers(J, s.d);
    const marks = {};
    members.forEach((m) => { marks[m.id] = A.cellOf(J, s, m, before ? s.d : A.addDays(s.d, -1)); });
    const unmarked = members.filter((m) => !A.markOf(J, s.key, m.id)).length;
    const absentNow = members.filter((m) => marks[m.id].code && marks[m.id].code.kind === 'a');
    let head = '', sub = [];
    if (s.cancelled) {
        head = `<p class="at-ans-v">${esc(t('cancelled'))}</p>`;
        sub.push(esc(s.why ? t('cancelled_why', { why: s.why }) : J.when.cancel[s.key] && J.when.cancel[s.key].moved ? t('moved_to', { date: fmt.dm(J.when.cancel[s.key].moved) }) : t('cancelled_sub')));
    } else if (!before) {
        head = `<p class="at-ans-v">${esc(t('expected', { n: members.length - absentNow.length, of: members.length }))}</p>`;
        sub.push(esc(t('future_sub')));
    } else {
        const pres = ss.present, unk = J.blank === 'unknown' ? members.filter((m) => marks[m.id].unknown).length : 0;
        const allHere = !absentNow.length && !unk && pres === members.length;
        const big = J.blank === 'none' ? t('lessons_n', { n: pres }) : allHere ? t('all_here', { n: members.length }) : t('came', { n: pres, of: members.length });
        head = `<p class="at-ans-v${allHere && J.blank !== 'none' ? ' ok' : absentNow.length && J.preset === 'camp' ? ' bad' : ''}">${esc(big)}</p>`;
        if (absentNow.length) sub.push(esc(t('absent_list', { n: absentNow.length })) + ' <b>' + esc(absentNow.slice(0, 6).map((m) => nameOf(m.id)).join(', ') + (absentNow.length > 6 ? '…' : '')) + '</b>');
        if (unk) sub.push(esc(tn('unmarked_n', unk)));
        const q = A.quorum(J, ss);
        if (q) sub.push('<b>' + esc(q.ok ? t('quorum_ok', { have: q.have, need: q.need }) : t('quorum_no', { have: q.have, need: q.need })) + '</b>');
    }
    const hdr = [head, sub.length ? `<p class="at-ans-s">${sub.join(' · ')}</p>` : ''];
    if (J.rules.birthdays) {
        const bd = A.birthdays(J, s.d, s.d);
        if (bd.length) hdr.push(`<p class="at-note good">🎂 ${esc(t('bday_today'))} <b>${esc(bd.map((b) => nameOf(b.mid) + (b.age != null && b.age < 30 ? ' (' + tn('age_n', b.age) + ')' : '')).join(', '))}</b></p>`);
    }
    ans.innerHTML = hdr.join('');
    const btns = [];
    if (!s.cancelled && unmarked && J.blank !== 'none') btns.push(`<button type="button" class="tc-btn" data-roll="all">${esc(absentNow.length ? t('all_present_confirm', { n: unmarked }) : t('all_present', { n: unmarked }))}</button>`);
    if (!s.cancelled && before && J.blank === 'none') btns.push(`<span class="tc-hint">${esc(t('tutor_hint'))}</span>`);
    act.innerHTML = btns.join('');
    // строки
    const brow = rowBadges(s, st, td);
    roll.innerHTML = members.map((m) => rowHtml(m, s, marks[m.id], brow[m.id] || [])).join('') || `<li class="at-empty">${esc(t('nobody_that_day'))}</li>`;
    tools.innerHTML = sessToolsHtml(s) + paintForecast(td);
}
function rowBadges(s, st, td) {
    const out = {};
    const streakAt = J.rules.streak || 3;
    const money = J.money && J.money.mode !== 'none';
    J.members.forEach((m) => {
        const b = [];
        const p = A.person(J, m.id);
        if (p.group) b.push(esc(p.group));
        const P = st.people[m.id];
        if (P && P.streak >= streakAt) b.push(`<span class="bad">${esc(tn('streak_n', P.streak))}</span>`);
        if (m.from && m.from > (J.start || '') && m.from >= A.addDays(s.d, -14)) b.push(esc(t('since', { date: fmt.dm(m.from) })));
        if (m.to && m.to >= s.d) b.push(esc(t('until', { date: fmt.dm(m.to) })));
        if (money) {
            // помесячная плата: в перекличке — только долг прошлых месяцев (текущий ещё оплачивают)
            const asOf = J.money.mode === 'month' ? A.addDays(td.slice(0, 8) + '01', -1) : td;
            const bal = asOf >= (J.start || '') ? A.balance(J, cal, m.id, asOf) : { due: 0, pass: null };
            if (bal.pass) {
                if (bal.pass.left < 0) b.push(`<span class="bad">${esc(t('pass_over', { n: -bal.pass.left }))}</span>`);
                else if (bal.pass.renew) b.push(`<span class="warn">${esc(tn('pass_left_n', bal.pass.left))}</span>`);
            } else if (bal.due > 0) b.push(`<span class="warn">${esc(t('due', { sum: fmt.money(bal.due) }))}</span>`);
        }
        if (J.rules.birthdays && p.birth) {
            const bd = A.birthdays({ members: [m], people: { [m.id]: p } }, A.addDays(s.d, 1), A.addDays(s.d, 7));
            if (bd.length) b.push('🎂 ' + esc(fmt.dm(bd[0].d)));
        }
        out[m.id] = b;
    });
    return out;
}
function rowHtml(m, s, x, badges) {
    const name = nameOf(m.id);
    const pc = A.presentCode(J);
    const c = x.code;
    const isP = c && c.kind === 'p', isA = c && c.kind === 'a';
    const implied = !!x.implied;
    const pOn = isP;
    const pLab = isP && !implied && c.id !== (pc && pc.id) ? c.k : '✓';
    const aLab = isA ? c.k : '✗';
    const pStyle = pOn && !implied ? ` style="background:${esc(c.color)}"` : '';
    const aStyle = isA ? ` style="background:${esc(c.color)}"` : '';
    const dis = s.cancelled ? ' disabled' : '';
    const st = isA ? (c.name || '') : isP ? (implied ? t('implied_present') : c.name) : x.unknown ? t('not_marked') : '';
    let more = '';
    if (ui.open === m.id && !s.cancelled) more = moreHtml(m, s, c);
    return `<li class="at-r${isA ? ' abs' : isP && !implied ? ' pres' : ''}" data-mid="${esc(m.id)}">
<div class="at-r-main"><span class="at-av" style="background:${AV[m.c % AV.length]}" aria-hidden="true">${esc(initials(name))}</span>
<div class="at-r-who"><span class="at-r-name">${esc(name)}</span><span class="at-r-sub">${badges.join('<span aria-hidden="true">·</span>')}${st && !badges.length ? '' : ''}</span></div>
<div class="at-r-btns" role="group" aria-label="${esc(name)}">
<button type="button" class="at-mk${pOn ? ' on' : ''}${implied ? ' imp' : ''}" data-act="p" aria-pressed="${pOn && !implied}"${pStyle}${dis} aria-label="${esc(t('mark_present') + ': ' + name)}">${esc(pLab)}<small>${esc(t('btn_here'))}</small></button>
<button type="button" class="at-mk${isA ? ' on' : ''}" data-act="a" aria-pressed="${isA}"${aStyle}${dis} aria-label="${esc(t('mark_absent') + ': ' + name)}">${esc(aLab)}<small>${esc(isA ? shortName(c.name) : t('btn_absent'))}</small></button>
</div></div>${more}</li>`;
}
function shortName(s) { s = String(s || ''); return s.length > 8 ? s.slice(0, 7) + '…' : s; }
function moreHtml(m, s, c) {
    const kind = ui.openKind || (c && c.kind) || 'a';
    const codes = J.codes.filter((x) => x.kind === kind || (kind === 'a' && x.kind === 'o'));
    const cur = A.markOf(J, s.key, m.id);
    const nt = A.noteOf(J, s.key, m.id) || {};
    const chips = codes.map((x) => `<button type="button" class="tc-chip${cur === x.id ? ' on' : ''}" data-code="${esc(x.id)}" aria-pressed="${cur === x.id}"><span class="k">${esc(x.k)}</span>${esc(x.name)}</button>`).join('');
    const code = A.code(J, cur);
    let fields = '';
    if (kind === 'a' && code && code.kind === 'a') {
        fields += `<div class="at-r-fields">`;
        if (code.ask) fields += `<label class="tc-f"><span class="tc-f-l">${esc(t('why_l'))}</span><input class="tc-in" data-f="why" type="text" maxlength="120" value="${esc(nt.why || '')}" placeholder="${esc(t('why_ph'))}"></label>`;
        if (code.ask === 'doc' || nt.doc) fields += `<label class="at-chk"><input type="checkbox" data-f="doc"${nt.doc ? ' checked' : ''}> ${esc(t('doc_l'))}</label>`;
        fields += `<label class="tc-f"><span class="tc-f-l">${esc(t('until_l'))}</span><input class="tc-in" data-f="until" type="date" min="${esc(A.addDays(s.d, 1))}"></label>`;
        fields += `</div>`;
    }
    return `<div class="at-r-more"><div class="at-chips" role="group" aria-label="${esc(kind === 'a' ? t('reason_l') : t('present_kind_l'))}">${chips}</div>${fields}
<div class="tc-actions"><button type="button" class="tc-btn sm ghost" data-roll="clear">${esc(t('clear_mark'))}</button><button type="button" class="tc-btn sm" data-roll="done">${esc(t('done'))}</button></div></div>`;
}
function sessToolsHtml(s) {
    const isExtra = s.extra;
    const can = J.when.cancel[s.key];
    return `<details class="tc-drawer at-st"${ui.setOpen.sess ? ' open' : ''} data-keep="sess"><summary><span class="tc-drawer-ico" aria-hidden="true">🗓️</span>${esc(t('sess_tools'))} <span class="tc-drawer-sub">${esc(t('sess_tools_sub'))}</span></summary><div class="tc-drawer-body">
<div class="at-row">${can ? `<button type="button" class="tc-btn sm ghost" data-roll="restore">${esc(t('restore_session'))}</button>` : `<label class="tc-f"><span class="tc-f-l">${esc(t('cancel_why'))}</span><input class="tc-in" id="at-cancel-why" type="text" maxlength="80" placeholder="${esc(t('cancel_why_ph'))}"></label><button type="button" class="tc-btn sm ghost" data-roll="cancel">${esc(t('cancel_session'))}</button>`}</div>
${can ? '' : `<div class="at-row"><label class="tc-f"><span class="tc-f-l">${esc(t('move_to'))}</span><input class="tc-in" id="at-move-d" type="date"></label><label class="tc-f"><span class="tc-f-l">${esc(t('move_time'))}</span><input class="tc-in" id="at-move-t" type="time" value="${esc(s.t || '')}"></label><button type="button" class="tc-btn sm ghost" data-roll="move">${esc(t('move_session'))}</button></div>`}
<div class="at-row"><label class="tc-f"><span class="tc-f-l">${esc(t('extra_time'))}</span><input class="tc-in" id="at-extra-t" type="time"></label><button type="button" class="tc-btn sm ghost" data-roll="add-session">${esc(t('add_session_day'))}</button>${isExtra ? `<button type="button" class="tc-btn sm ghost at-danger" data-roll="drop">${esc(t('drop_session'))}</button>` : ''}</div>
</div></details>`;
}
function paintForecast(td) {
    if (!J.rules.forecast || ui.date !== td || !J.members.length) return '';
    const f = A.forecast(J, cal, td);
    if (!f) return '';
    const bits = [t('fc_expected', { n: f.expected, of: f.total })];
    if (f.marked) bits.push(tn('fc_marked', f.marked));
    if (f.likely) bits.push(tn('fc_likely', f.likely));
    return `<p class="at-note${f.likely ? ' warn' : ''}" style="margin-top:12px">🍽️ <b>${esc(t('fc_head', { day: fmt.full(f.session.d) }))}</b> ${esc(bits.join(' · '))}<button type="button" class="at-link" data-goto="${esc(f.session.d)}">${esc(t('fc_mark'))}</button></p>`;
}
function rollClick(e) {
    const go = e.target.closest('[data-go]');
    if (go) { if (go.dataset.open) ui.setOpen[go.dataset.open] = true; setView(go.dataset.go, true); return; }
    const gt = e.target.closest('[data-goto]');
    if (gt) { ui.date = gt.dataset.goto; ui.sk = ''; ui.open = ''; fu('forecast_go'); paintAll(); return; }
    const sk = e.target.closest('[data-sk]');
    if (sk) { ui.sk = sk.dataset.sk; ui.open = ''; paintRoll(); return; }
    const { s } = curSession();
    const r = e.target.closest('[data-roll]');
    if (r) { rollAction(r.dataset.roll, s, e); return; }
    const row = e.target.closest('.at-r');
    if (!row || !s) return;
    const mid = row.dataset.mid;
    const mk = e.target.closest('[data-act]');
    const chip = e.target.closest('[data-code]');
    if (chip) {
        const c = A.code(J, chip.dataset.code);
        change(() => A.setMark(J, s.key, mid, c.id), paintRoll);
        fu('reason', { tpl: c.tpl || 'own', kind: c.kind });
        if (!c.ask && c.kind === 'a') ui.open = '';
        if (c.kind === 'p') ui.open = '';
        paintRoll(); refocus(mid, 'a');
        return;
    }
    if (!mk || s.cancelled) return;
    const act = mk.dataset.act;
    const cur = A.markOf(J, s.key, mid), cc = cur ? A.code(J, cur) : null;
    if (act === 'p') {
        if (cc && cc.kind === 'p') {
            // второе касание по «был»: варианты присутствия (опоздал, пробное…) и «снять»
            ui.open = ui.open === mid && ui.openKind === 'p' ? '' : mid; ui.openKind = 'p';
            paintRoll(); refocus(mid, 'p'); return;
        }
        const p = A.presentCode(J);
        change(() => A.setMark(J, s.key, mid, p.id), paintRoll);
        if (ui.open === mid) ui.open = '';
        firstMark('p');
        paintRoll(); refocus(mid, 'p');
    } else {
        if (cc && cc.kind === 'a') { ui.open = ui.open === mid && ui.openKind === 'a' ? '' : mid; ui.openKind = 'a'; paintRoll(); refocus(mid, 'a'); return; }
        const a = A.absentCode(J);
        if (!a) return;
        change(() => A.setMark(J, s.key, mid, a.id), paintRoll);
        ui.open = mid; ui.openKind = 'a';
        firstMark('a');
        paintRoll(); refocus(mid, 'a');
    }
}
let firstMarkDone = false;
function firstMark(kind) { if (firstMarkDone) return; firstMarkDone = true; stage('roll_mark', { kind }); }
function refocus(mid, act) {
    const b = document.querySelector('#at-roll .at-r[data-mid="' + cssEsc(mid) + '"] [data-act="' + act + '"]');
    if (b) b.focus({ preventScroll: true });
}
function rollAction(a, s, e) {
    if (a === 'all' && s) {
        let n = 0;
        change(() => { n = A.allPresent(J, s); }, paintRoll);
        ui.open = '';
        fu('all_present', { n: bucket(n), people: bucket(J.members.length) });
        stage('roll_all', { n: bucket(n) });
        try { if (window.__aeFunnel) window.__aeFunnel('roll_done', { n: bucket(J.members.length) }); } catch (_) {}
        toast(tn('marked_n', n), 'success');
        return;
    }
    if (a === 'done') { ui.open = ''; paintRoll(); return; }
    if (a === 'clear' && s) { const mid = ui.open; change(() => A.clearMark(J, s.key, mid), paintRoll); ui.open = ''; paintRoll(); return; }
    if (a === 'add-session') {
        const tIn = $('at-extra-t');
        const tv = tIn && tIn.value ? tIn.value : '';
        let key = '';
        change(() => { key = A.addSession(J, ui.date, { t: tv }); });
        ui.sk = key; fu('add_session', { mode: J.when.mode });
        paintAll();
        return;
    }
    if (!s) return;
    if (a === 'cancel') { const why = ($('at-cancel-why') || {}).value || ''; change(() => A.cancelSession(J, s.key, why)); fu('cancel_session'); toast(t('cancel_done'), 'info'); return; }
    if (a === 'restore') { change(() => A.restoreSession(J, s.key)); fu('restore_session'); return; }
    if (a === 'drop') { if (!window.confirm(t('drop_q'))) return; change(() => A.dropExtra(J, s.key)); ui.sk = ''; fu('drop_session'); return; }
    if (a === 'move') {
        const d = ($('at-move-d') || {}).value, tm = ($('at-move-t') || {}).value || '';
        if (!A.isIso(d)) { toast(t('move_need_date'), 'warning'); return; }
        let nk = '';
        change(() => { nk = A.moveSession(J, cal, s.key, d, tm); });
        if (nk) { ui.date = d; ui.sk = nk; fu('move_session'); toast(t('moved_done', { date: fmt.dm(d) }), 'success'); paintAll(); }
    }
}
function rollInput(e) {
    const f = e.target.dataset.f;
    if (!f || e.type !== 'change') return;
    const row = e.target.closest('.at-r');
    const { s } = curSession();
    if (!row || !s) return;
    const mid = row.dataset.mid;
    if (f === 'why' || f === 'doc') {
        const nt = A.noteOf(J, s.key, mid) || {};
        const next = { why: f === 'why' ? e.target.value : nt.why, doc: f === 'doc' ? e.target.checked : nt.doc };
        const before = snap();
        A.setNote(J, s.key, mid, next);
        if (snap() !== before) { push(before); touch(); }
        if (f === 'doc') fu('doc_mark', { on: e.target.checked ? 1 : 0 });
    }
    if (f === 'until' && e.type === 'change') {
        const to = e.target.value;
        const cid = A.markOf(J, s.key, mid);
        if (!A.isIso(to) || !cid) return;
        let n = 0;
        change(() => { n = A.markRange(J, cal, mid, s.d, to, cid); }, paintRoll);
        fu('range_mark', { n: bucket(n), via: 'roll' });
        toast(tn('range_done', n, { date: fmt.dm(to) }), 'success');
    }
}

/* ── месяц: сетка табеля ──────────────────────────────────────────────── */
let monthS = [];
function brushCode() { return ui.brush === 'clear' ? null : (A.code(J, ui.brush) || A.absentCode(J)); }
function paintBrush() {
    const cur = ui.brush || (A.absentCode(J) || {}).id;
    const html = J.codes.map((c) => `<button type="button" class="at-b" data-b="${esc(c.id)}" aria-pressed="${cur === c.id}" title="${esc(c.name)}"><i style="background:${tint(c.color, 0.22)};color:color-mix(in srgb,${esc(c.color)} 62%,var(--lg-text))">${esc(c.k)}</i><span class="at-bn">${esc(c.name)}</span></button>`).join('')
        + `<button type="button" class="at-b" data-b="clear" aria-pressed="${cur === 'clear'}">${esc(t('brush_clear'))}</button>`
        + `<button type="button" class="at-b" id="at-range" aria-pressed="${ui.range}">${esc(t('brush_range'))}</button>`;
    $('at-brush').innerHTML = html;
}
function paintMonth() {
    const [y, m] = ui.month;
    const td = today();
    monthS = A.monthSessions(J, cal, y, m);
    paintBrush();
    const [a, b] = A.monthBounds(y, m);
    const st = A.stats(J, cal, a, b, td);
    const held = monthS.filter((s) => !s.cancelled && s.d <= td).length;
    const P = Object.values(st.people);
    const pres = P.reduce((x, p) => x + p.present, 0), abs = P.reduce((x, p) => x + p.absent, 0), exc = P.reduce((x, p) => x + p.exc, 0);
    $('at-msum').textContent = monthS.length
        ? [tn('sessions_n', monthS.length), held < monthS.length ? t('held_of', { n: held }) : '', pres + abs ? t('avg_pct', { p: fmt.num(Math.round(pres / (pres + abs) * 1000) / 10) }) : '', abs ? tn('absences_n', abs) + (exc ? ' (' + t('exc_n', { n: exc }) + ')' : '') : ''].filter(Boolean).join(' · ')
        : '';
    if (!grid) {
        grid = createGrid($('at-grid'), {
            onPaint: applyPaint,
            onReset: (R) => { const b0 = ui.brush; ui.brush = 'clear'; applyPaint(R); ui.brush = b0; },
            onName: (pid) => { if (pid === '__sum') return; ui.pOpen = pid; setView('people', true); },
            onFocus: () => {},
            onRangeStart: (pid, d) => { const s = monthS[d - 1]; $('at-binfo').textContent = t('range_start', { who: nameOf(pid), date: s ? fmt.dm(s.d) : '' }); },
        });
    }
    $('at-grid').style.setProperty('--at-nt', '3');
    const ci = {};
    J.codes.forEach((c, i) => { ci[c.id] = i; });
    const members = J.members.filter((mm) => (!mm.from || mm.from <= b) && (!mm.to || mm.to >= a));
    const rows = members.map((mm) => {
        const P1 = st.people[mm.id];
        const cells = monthS.map((s) => {
            const x = A.cellOf(J, s, mm, td);
            const nm = nameOf(mm.id) + ', ' + fmt.dm(s.d) + (sessLabel(s) ? ' ' + sessLabel(s) : '');
            if (x.out) return { cls: 'out', top: '', bottom: '', aria: nm + ': ' + t('out_cell') };
            if (x.cancelled) return { cls: 'cx', top: '–', bottom: '', aria: nm + ': ' + t('cancelled') };
            if (x.unknown) return { cls: 'unk', top: '?', bottom: '', aria: nm + ': ' + t('not_marked') };
            if (!x.code) return { cls: '', top: '', bottom: '', aria: nm };
            const nt = A.noteOf(J, s.key, mm.id);
            if (x.implied) return { cls: 'imp', top: '·', bottom: '', aria: nm + ': ' + t('implied_present') };
            return { cls: 'k-' + ci[x.code.id] + (nt ? ' nt' : ''), top: x.code.k, bottom: '', title: x.code.name + (nt && nt.why ? ' — ' + nt.why : '') + (nt && nt.doc ? ' · ' + t('doc_short') : ''), aria: nm + ': ' + x.code.name };
        });
        const pctBad = J.rules.threshold && P1.pct != null && P1.pct < J.rules.threshold;
        return {
            pid: mm.id, label: nameOf(mm.id), sub: A.person(J, mm.id).group || '', title: nameOf(mm.id), sel: false, cells,
            tot: [{ v: String(P1.present) }, { v: String(P1.absent), cls: P1.maxStreak >= (J.rules.streak || 3) ? 'bad' : '' }, { v: P1.pct == null ? '' : Math.round(P1.pct) + '%', cls: pctBad ? 'bad' : '' }],
        };
    });
    if (rows.length && monthS.length) {
        rows.push({ pid: '__sum', label: t('row_present'), sub: '', title: '', cells: monthS.map((s) => {
            const x = st.bySession[s.key] || {};
            return s.cancelled ? { cls: 'cx sum', top: '–', bottom: '' } : { cls: 'sum', top: s.d <= td || x.present ? String(x.present || 0) : '', bottom: '', aria: fmt.dm(s.d) + ': ' + t('row_present') + ' ' + (x.present || 0) };
        }), tot: [{ v: '' }, { v: '' }, { v: '' }] });
    }
    grid.render({
        days: monthS.length,
        label: t('grid_l', { month: fmt.month(y, m) }),
        nameHead: tn('people_n', members.length),
        head: monthS.map((s) => ({ d: fmt.day(s.d), dow: sessLabel(s) || fmt.dow(s.d), kind: (cal.holiday(s.d) ? 'holiday' : s.cancelled ? 'we' : '') + (s.d === td ? ' today' : ''), title: fmt.full(s.d) + (sessLabel(s) ? ' · ' + sessLabel(s) : '') + (s.cancelled ? ' · ' + t('cancelled') : '') })),
        totHead: [{ label: '✓', title: t('present_total') }, { label: '✗', title: t('absent_total') }, { label: '%', title: t('pct_total') }],
        rows,
        empty: !monthS.length ? (J.when.mode === 'schedule' && !(J.when.slots || []).length ? t('grid_no_schedule') : t('grid_no_sessions')) : t('grid_no_people'),
    });
    grid.setRange(ui.range);
}
function applyPaint(R) {
    if (!R || !R.pids) return;
    const brush = ui.brush || (A.absentCode(J) || {}).id;
    const c = brush === 'clear' ? null : A.code(J, brush);
    let n = 0;
    change(() => {
        R.pids.forEach((pid) => {
            if (pid === '__sum') return;
            const mm = J.members.find((x) => x.id === pid);
            if (!mm) return;
            for (let d = R.d0; d <= R.d1; d++) {
                const s = monthS[d - 1];
                if (!s || s.cancelled || !A.active(mm, s.d)) continue;
                const cur = A.markOf(J, s.key, pid);
                if (!c) { if (cur) { A.clearMark(J, s.key, pid); n++; } continue; }
                // касание по той же отметке снимает её (одна клетка)
                if (R.d0 === R.d1 && R.pids.length === 1 && cur === c.id && R.via === 'tap') { A.clearMark(J, s.key, pid); n++; continue; }
                A.setMark(J, s.key, pid, c.id); n++;
            }
        });
    }, paintMonth);
    if (R.via !== 'tap') fu('range_mark', { n: bucket(n), via: R.via });
    $('at-binfo').textContent = '';
    if (n) firstMark('grid');
}

/* ── итоги и деньги ───────────────────────────────────────────────────── */
function periodRange() {
    const [y, m] = ui.month;
    const td = today();
    if (ui.period === 'quarter') { const q0 = Math.floor((m - 1) / 3) * 3 + 1; return [A.monthBounds(y, q0)[0], A.monthBounds(y, q0 + 2)[1]]; }
    if (ui.period === 'all') return [J.start || J.created || td, td > (J.start || td) ? td : (J.start || td)];
    if (ui.period === 'custom') {
        const a = A.isIso(ui.pFrom) ? ui.pFrom : A.monthBounds(y, m)[0], b = A.isIso(ui.pTo) ? ui.pTo : A.monthBounds(y, m)[1];
        return a <= b ? [a, b] : [b, a];
    }
    return A.monthBounds(y, m);
}
function paintSum() {
    const td = today();
    const [a, b] = periodRange();
    document.querySelectorAll('#at-period .tc-seg-b').forEach((x) => { const on = x.dataset.v === ui.period; x.classList.toggle('on', on); x.setAttribute('aria-pressed', String(on)); });
    $('at-sum-mnav').hidden = ui.period === 'all' || ui.period === 'custom';
    $('at-custom').hidden = ui.period !== 'custom';
    if (ui.period === 'custom') { $('at-p-from').value = a; $('at-p-to').value = b; }
    if (ui.period === 'quarter') { const q = Math.floor((ui.month[1] - 1) / 3) + 1; $('at-sum-mnav').querySelector('[data-mname]').textContent = t('quarter_n', { q, y: ui.month[0] }); }
    const st = A.stats(J, cal, a, b, td);
    const held = st.sessions.filter((s) => !s.cancelled && s.d <= td).length;
    const money = J.money && J.money.mode !== 'none';
    const P = J.members.map((m) => ({ m, s: st.people[m.id], bal: money ? A.balance(J, cal, m.id, td) : null }));
    const pres = P.reduce((x, p) => x + p.s.present, 0), abs = P.reduce((x, p) => x + p.s.absent, 0), exc = P.reduce((x, p) => x + p.s.exc, 0);
    const debtors = money ? P.filter((p) => p.bal.due > 0 || (p.bal.pass && p.bal.pass.left <= 0)) : [];
    const debtSum = money ? P.reduce((x, p) => x + Math.max(0, p.bal.due), 0) : 0;
    const tiles = [
        tile(t('tile_pct'), pres + abs ? fmt.num(Math.round(pres / (pres + abs) * 1000) / 10) + '%' : '—', tn('sessions_held_n', held), pres + abs && pres / (pres + abs) < 0.7 ? 'warn' : 'good lead'),
        tile(t('tile_abs'), String(abs), exc ? t('exc_n', { n: exc }) : t('tile_abs_sub'), ''),
    ];
    if (money) tiles.push(tile(t('tile_debt'), debtSum ? fmt.money(debtSum) : '0', tn('debtors_n', debtors.length), debtors.length ? 'warn' : 'good'));
    const unk = P.reduce((x, p) => x + p.s.unknown, 0);
    if (J.blank === 'unknown' && unk) tiles.push(tile(t('tile_unk'), String(unk), t('tile_unk_sub'), 'warn'));
    $('at-tiles').innerHTML = tiles.join('');
    const streakAt = J.rules.streak || 3;
    const thr = +J.rules.threshold || 0;
    const filters = [['all', t('flt_all')], ['streak', t('flt_streak')]];
    if (thr) filters.push(['below', t('flt_below', { p: thr })]);
    if (money) filters.push(['debt', t('flt_debt')]);
    if (!filters.some((f) => f[0] === ui.filter)) ui.filter = 'all';
    $('at-sum-filters').innerHTML = filters.map((f) => `<button type="button" class="tc-chip${ui.filter === f[0] ? ' on' : ''}" data-filter="${f[0]}" aria-pressed="${ui.filter === f[0]}">${esc(f[1])}</button>`).join('');
    const list = P.filter((p) => ui.filter === 'all' || (ui.filter === 'streak' && p.s.maxStreak >= streakAt) || (ui.filter === 'below' && p.s.pct != null && p.s.pct < thr) || (ui.filter === 'debt' && (p.bal.due > 0 || (p.bal.pass && p.bal.pass.left <= 0))));
    const mode = J.money.mode;
    const head = ['№', t('col_name'), t('col_present'), t('col_absent'), t('col_exc'), '%', t('col_streak')];
    if (thr) head.push(t('col_admit'));
    if (money) { head.push(t('col_charged'), t('col_paid')); if (mode === 'pass') head.push(t('col_pass')); head.push(t('col_due'), ''); }
    const rowsH = list.map((p, i) => {
        const s = p.s;
        const cells = [`<td class="num">${i + 1}</td>`, `<td class="nm">${esc(nameOf(p.m.id))} <button type="button" class="at-st-b" data-st="${esc(p.m.id)}" title="${esc(t('st_btn_l'))}">${esc(t('st_btn'))}</button></td>`, `<td class="num">${s.present}</td>`, `<td class="num">${s.absent}</td>`, `<td class="num">${s.exc}</td>`,
            `<td class="num${thr && s.pct != null && s.pct < thr ? ' bad' : ''}">${s.pct == null ? '—' : fmt.num(s.pct) + '%'}</td>`,
            `<td class="num${s.streak >= streakAt ? ' bad' : ''}" title="${esc(t('streak_max', { n: s.maxStreak }))}">${s.streak || '—'}</td>`];
        if (thr) { const ad = A.admitted(J, s); cells.push(`<td class="${ad === false ? 'bad' : ad ? 'ok' : ''}">${ad == null ? '—' : ad ? esc(t('admit_yes')) : esc(t('admit_no'))}</td>`); }
        if (money) {
            const ch = periodCharge(p.m.id, a, b, td);
            const paid = (A.person(J, p.m.id).pays || []).filter((x) => x.d >= a && x.d <= b).reduce((z, x) => z + (+x.sum || 0), 0);
            cells.push(`<td class="num">${ch ? esc(fmt.money(ch)) : '—'}</td>`, `<td class="num">${paid ? esc(fmt.money(paid)) : '—'}</td>`);
            if (mode === 'pass') { const ps = p.bal.pass; cells.push(`<td class="num${ps && ps.left <= 0 ? ' bad' : ps && ps.renew ? ' warn' : ''}">${ps ? ps.left : '—'}</td>`); }
            const due = p.bal.due;
            cells.push(`<td class="num${due > 0 ? ' bad' : due < 0 ? ' ok' : ''}">${due > 0 ? esc(fmt.money(due)) : due < 0 ? esc(t('overpaid', { sum: fmt.money(-due) })) : '0'}</td>`);
            cells.push(`<td><button type="button" class="at-pay-b" data-pay="${esc(p.m.id)}" aria-expanded="${ui.pay === p.m.id}">${esc(t('pay_add'))}</button></td>`);
        }
        let row = `<tr data-mid="${esc(p.m.id)}">${cells.join('')}</tr>`;
        if (money && ui.pay === p.m.id) row += `<tr class="payrow"><td colspan="${head.length}">${payForm(p.m.id)}</td></tr>`;
        return row;
    });
    const emptyMsg = J.members.length ? t('nobody_filter') : t('roll_nobody');
    const emptyRow = `<tr><td colspan="${head.length}" class="at-empty">${esc(emptyMsg)}</td></tr>`;
    const headRow = head.map((h) => `<th scope="col">${esc(h)}</th>`).join('');
    $('at-sum').innerHTML = `<thead><tr>${headRow}</tr></thead><tbody>${rowsH.length ? rowsH.join('') : emptyRow}</tbody>`;
    // по занятиям, именинники, без переклички
    const extra = [];
    const ses = st.sessions.filter((s) => s.d <= td);
    if (ses.length > 1 && ses.length <= 130) {
        const max = Math.max(1, ...ses.map((s) => (st.bySession[s.key] || {}).n || 0));
        extra.push(`<div class="at-box"><h3>${esc(t('by_session'))}</h3><div class="at-bars" role="img" aria-label="${esc(t('by_session'))}">${ses.map((s) => { const x = st.bySession[s.key] || {}; return s.cancelled ? '<span class="cx" style="height:6%"></span>' : `<span style="height:${Math.max(3, Math.round((x.present || 0) / max * 100))}%" title="${esc(fmt.dm(s.d) + (sessLabel(s) ? ' ' + sessLabel(s) : '') + ': ' + (x.present || 0) + ' / ' + (x.n || 0))}"></span>`; }).join('')}</div></div>`);
    }
    if (J.rules.birthdays) {
        const bd = A.birthdays(J, a, b);
        if (bd.length) extra.push(`<div class="at-box"><h3>🎂 ${esc(t('bdays'))}</h3><ul>${bd.map((x) => `<li>${esc(fmt.dm(x.d))} — ${esc(nameOf(x.mid))}${x.age != null && x.age < 30 ? ' (' + esc(tn('age_n', x.age)) + ')' : ''}</li>`).join('')}</ul></div>`);
    }
    if (J.blank === 'unknown') {
        const miss = ses.filter((s) => !s.cancelled && (st.bySession[s.key] || {}).unknown);
        if (miss.length) extra.push(`<div class="at-box"><h3>${esc(t('no_roll'))}</h3><ul>${miss.slice(-12).map((s) => `<li><button type="button" class="at-link" data-goto="${esc(s.d)}" data-sk="${esc(s.key)}">${esc(fmt.full(s.d) + (sessLabel(s) ? ' · ' + sessLabel(s) : ''))}</button> — ${esc(tn('unmarked_n', (st.bySession[s.key] || {}).unknown))}</li>`).join('')}</ul></div>`);
    }
    $('at-sum-extra').innerHTML = extra.join('');
}
function tile(l, v, s, cls) { return `<div class="tc-stat ${cls || ''}"><div class="tc-stat-l">${esc(l)}</div><div class="tc-stat-v">${esc(v)}</div><div class="tc-stat-s">${esc(s)}</div></div>`; }
function periodCharge(mid, a, b, td) {
    let y = +a.slice(0, 4), m = +a.slice(5, 7), sum = 0, g = 0;
    const ey = +b.slice(0, 4), em = +b.slice(5, 7);
    while ((y < ey || (y === ey && m <= em)) && g++ < 120) { sum += A.monthCharge(J, cal, mid, y, m, td); m++; if (m > 12) { m = 1; y++; } }
    return Math.round(sum * 100) / 100;
}
function payForm(mid) {
    const p = A.person(J, mid);
    const M = J.money;
    const pass = M.mode === 'pass';
    const list = (p.pays || []).slice().reverse().slice(0, 12);
    return `<form class="at-payf" data-payf="${esc(mid)}"><label class="tc-f"><span class="tc-f-l">${esc(t('pay_date'))}</span><input class="tc-in" name="d" type="date" value="${esc(today())}" required></label>
<label class="tc-f"><span class="tc-f-l">${esc(t('pay_sum'))}</span><input class="tc-in" name="sum" type="number" min="0" step="0.01" inputmode="decimal" value="${esc(pass ? (M.passPrice || '') : M.mode === 'month' ? (M.fee || '') : '')}" required></label>
${pass ? `<label class="tc-f"><span class="tc-f-l">${esc(t('pay_n'))}</span><input class="tc-in" name="n" type="number" min="0" step="1" inputmode="numeric" value="${esc(M.passN || 8)}"></label>` : ''}
<button type="submit" class="tc-btn sm">${esc(t('pay_save'))}</button></form>
${list.length ? `<ul class="at-pays">${list.map((x) => `<li><span>${esc(fmt.date(x.d))}</span><b>${esc(fmt.money(x.sum))}</b>${x.n ? `<span>${esc(tn('sessions_n', x.n))}</span>` : ''}<button type="button" class="at-x" data-paydel="${esc(x.id)}" data-mid="${esc(mid)}" aria-label="${esc(t('pay_del'))}">×</button></li>`).join('')}</ul>` : ''}`;
}

/* ── участники ────────────────────────────────────────────────────────── */
/* Заголовки списков на любом из языков сайта: импорт узнаёт колонку, на каком бы языке её ни назвали. */
const ALIASES = {
    name: ['фио', 'ф.и.о', 'ф и о', 'фамилия имя', 'фамилия, имя', 'фамилия и имя', 'фамилия, имя ребенка', 'фамилия, имя ребёнка', 'ребенок', 'ребёнок', 'дети', 'ученик', 'учащийся', 'студент', 'участник', 'воспитанник', 'спортсмен', 'член',
        'піб', 'прізвище та ім\'я', 'дитина', 'учень', 'name', 'full name', 'student', 'student name', 'child', 'pupil', 'participant', 'member', 'athlete', 'camper',
        'nombre', 'nombre completo', 'alumno', 'estudiante', 'participante', 'nom', 'nom complet', 'élève', 'enfant', 'participant', 'nome', 'nome completo', 'aluno', 'criança',
        'schüler', 'kind', 'teilnehmer', 'vorname nachname', 'nama', 'nama lengkap', 'siswa', 'murid', 'peserta', '이름', '성명', '학생', '참가자', 'الاسم', 'اسم الطالب', 'الطالب', 'المشارك'],
    group: ['группа', 'класс', 'подгруппа', 'отряд', 'команда', 'група', 'клас', 'group', 'class', 'team', 'squad', 'grupo', 'clase', 'equipo', 'groupe', 'classe', 'équipe', 'turma', 'equipe', 'gruppe', 'klasse', 'mannschaft', 'kelompok', 'kelas', 'tim', '반', '그룹', '팀', 'المجموعة', 'الصف', 'الفريق'],
    birth: ['дата рождения', 'день рождения', 'др', 'родился', 'дата народження', 'birthday', 'date of birth', 'dob', 'birth date', 'fecha de nacimiento', 'cumpleaños', 'date de naissance', 'anniversaire', 'data de nascimento', 'aniversário', 'geburtsdatum', 'geburtstag', 'tanggal lahir', '생년월일', '생일', 'تاريخ الميلاد'],
    phone: ['телефон', 'телефон родителя', 'тел', 'тел.', 'контакт', 'телефон батьків', 'phone', 'parent phone', 'mobile', 'telephone', 'contact', 'teléfono', 'celular', 'téléphone', 'portable', 'telefone', 'telefon', 'handy', 'nomor telepon', 'telepon', 'hp', '전화', '전화번호', '연락처', 'الهاتف', 'رقم الهاتف', 'الجوال'],
    account: ['номер счета', 'номер счёта', 'лицевой счет', 'лицевой счёт', 'л/с', 'account', 'account no'],
    disc: ['льгота', 'льгота, %', 'скидка', 'скидка, %', 'пільга', 'знижка', 'discount', 'discount, %', 'descuento', 'remise', 'réduction', 'desconto', 'rabatt', 'ermäßigung', 'diskon', 'potongan', '할인', 'خصم'],
    from: ['с', 'дата зачисления', 'зачислен', 'дата прихода', 'з', 'from', 'start', 'joined', 'start date', 'desde', 'fecha de ingreso', 'depuis', 'à partir de', 'a partir de', 'seit', 'ab', 'mulai', '시작', 'من'],
    to: ['по', 'дата отчисления', 'выбыл', 'отчислен', 'до', 'to', 'left', 'end', 'end date', 'hasta', 'jusqu\'à', 'até', 'bis', 'sampai', '종료', 'إلى'],
    note: ['заметка', 'примечание', 'комментарий', 'примітка', 'note', 'notes', 'comment', 'nota', 'notas', 'comentario', 'remarque', 'commentaire', 'observação', 'notiz', 'bemerkung', 'catatan', 'keterangan', '메모', '비고', 'ملاحظة', 'ملاحظات'],
};
/* «Имя» одной колонкой — второй проход: в первом «Фамилия | Имя» склеивает импорт списка (staff-roster). */
const FIRST_NAME_ONLY = ['имя', 'ім\'я', 'first name', 'given name'];
function fields(second) {
    const L = I.fields || {};
    const al = (k) => (second && k === 'name' ? ALIASES[k].concat(FIRST_NAME_ONLY) : ALIASES[k] || []);
    return [
        { key: 'fio', label: L.name || 'Name', kind: 'text', required: true, width: 34, example: L.name_ex || '', aliases: al('name') },
        { key: 'group', label: L.group || 'Group', kind: 'text', width: 14, aliases: al('group') },
        { key: 'birth', label: L.birth || 'Date of birth', kind: 'date', width: 13, aliases: al('birth') },
        { key: 'phone', label: L.phone || 'Phone', kind: 'text', width: 18, aliases: al('phone') },
        { key: 'account', label: L.account || 'Account no.', kind: 'text', width: 13, aliases: al('account') },
        { key: 'disc', label: L.disc || 'Discount, %', kind: 'num', width: 10, aliases: al('disc') },
        { key: 'from', label: L.from || 'From', kind: 'date', width: 13, aliases: al('from') },
        { key: 'to', label: L.to || 'To', kind: 'date', width: 13, aliases: al('to') },
        { key: 'note', label: L.note || 'Note', kind: 'text', width: 30, aliases: al('note') },
    ];
}
/** Записи импорта (поле fio, даты номером дня) → записи журнала. */
function toPeople(records) {
    return (records || []).map((r) => {
        const o = { name: r.fio || '' };
        ['group', 'phone', 'account', 'note'].forEach((k) => { if (r[k] != null && r[k] !== '') o[k] = String(r[k]); });
        if (r.disc != null) o.disc = r.disc;
        ['birth', 'from', 'to'].forEach((k) => { if (typeof r[k] === 'number') o[k] = A.isoOf(r[k]); });
        return o;
    }).filter((o) => o.name);
}
function applyPeople(list, via) {
    const box = $('at-imp');
    if (!list.length) { box.hidden = false; box.classList.add('bad'); box.textContent = t('imp_none'); diag('import_empty', { via }); return; }
    let res = { added: 0, updated: 0 };
    change(() => { res = A.mergePeople(J, list); });
    box.hidden = false; box.classList.remove('bad');
    box.textContent = t('imp_done', { added: res.added, updated: res.updated });
    fu(via === 'file' ? 'import_file' : via === 'paste' ? 'import_paste' : 'import_' + via, { n: bucket(list.length), added: bucket(res.added) });
    peopleStage();
    paintAll();
}
let peopled = false;
function peopleStage() {
    if (peopled || !J || !J.members.length) return;
    peopled = true;
    stage('people', { n: bucket(J.members.length), p: J.preset });
    try { if (window.__aeFunnel) window.__aeFunnel('form_start', { n: bucket(J.members.length) }); } catch (_) {}
}
function importTextBlock(text, via) {
    const plain = A.plainNames(text);
    if (plain) { applyPeople(plain, via); return; }
    let r = rosterText(text, fields());
    if (!r.records.length) r = rosterText(text, fields(true));
    applyPeople(toPeople(r.records), via);
}
async function onFile(file, where) {
    if (!file) return;
    const box = $('at-imp');
    const name = String(file.name || '').toLowerCase();
    try {
        if (/\.json$/.test(name) || file.type === 'application/json') { await openJournalFile(file); return; }
        if (!J) return;
        const kind = rosterKind(file);
        if (kind === 'text') {
            const buf = await file.arrayBuffer();
            const dec = decodeBytes(new Uint8Array(buf));
            importTextBlock(dec && dec.text != null ? dec.text : String(dec || ''), 'file');
            return;
        }
        let res = await readRosterFile(file, fields(), { sheetjs: CFG.sheetjs, keepRows: true });
        if (!res.records.length) res = await readRosterFile(file, fields(true), { sheetjs: CFG.sheetjs, keepRows: true });
        let list = toPeople(res.records);
        if (!list.length && res.rows) {
            // одна колонка имён без шапки
            const plain = A.plainNames(res.rows.map((r) => (r && r[0] != null ? String(r[0]) : '')).join('\n'));
            list = plain || [];
        }
        applyPeople(list, 'file');
    } catch (err) {
        const code = err && err.code;
        if (box) { box.hidden = false; box.classList.add('bad'); box.textContent = code === 'bad_type' ? t('imp_bad_type') : t('imp_failed'); }
        diag('import_failed', { code: code || 'error', where });
        if (!code && window.__handleError) window.__handleError(err, 'attendance_import', { say: t('imp_failed') });
    }
}
function paintPeople() {
    const list = $('at-plist');
    const q = A.nameKey(ui.search);
    const rows = J.members.map((m, i) => ({ m, i })).filter((x) => !q || A.nameKey(nameOf(x.m.id)).indexOf(q) >= 0 || A.nameKey(A.person(J, x.m.id).group || '').indexOf(q) >= 0);
    list.innerHTML = rows.map(({ m, i }) => {
        const p = A.person(J, m.id);
        const open = ui.pOpen === m.id;
        const meta = [p.group, p.birth ? '🎂 ' + fmt.date(p.birth) : '', m.from ? t('since', { date: fmt.date(m.from) }) : '', m.to ? t('until', { date: fmt.date(m.to) }) : ''].filter(Boolean).join(' · ');
        return `<li class="at-pi" data-mid="${esc(m.id)}"><div class="at-pi-row"><span class="at-pi-no">${i + 1}</span><span class="at-av" style="background:${AV[m.c % AV.length]}" aria-hidden="true">${esc(initials(p.name))}</span>
<label class="at-pi-name"><span class="tc-sr">${esc(t('f_name'))}</span><input class="tc-in" data-k="name" type="text" maxlength="120" value="${esc(p.name || '')}" placeholder="${esc(t('noname'))}"></label>
<span class="at-pi-meta">${esc(meta)}</span><button type="button" class="at-ib" data-pact="open" aria-expanded="${open}" aria-label="${esc(t('more_fields'))}">⋯</button></div>
${open ? personEditor(m, p) : ''}</li>`;
    }).join('') || `<li class="at-empty">${esc(J.members.length ? t('nobody_filter') : t('people_empty'))}</li>`;
    probeOthers();
}
function personEditor(m, p) {
    const f = I.fields || {};
    const inp = (k, type, v, extra) => `<label class="tc-f${extra && extra.wide ? ' wide' : ''}"><span class="tc-f-l">${esc(f[k] || k)}</span><input class="tc-in" data-k="${k}" type="${type}" value="${esc(v == null ? '' : v)}"${extra && extra.attrs ? ' ' + extra.attrs : ''}></label>`;
    return `<div class="at-pi-ed">${inp('group', 'text', p.group, { attrs: 'maxlength="60"' })}${inp('birth', 'date', p.birth)}${inp('phone', 'tel', p.phone, { attrs: 'maxlength="40" autocomplete="off"' })}
${J.money.mode !== 'none' ? inp('disc', 'number', p.disc, { attrs: 'min="0" max="100" step="1" inputmode="numeric"' }) : ''}${RU ? inp('account', 'text', p.account, { attrs: 'maxlength="30"' }) : ''}
${inp('from', 'date', m.from)}${inp('to', 'date', m.to)}${inp('note', 'text', p.note, { wide: true, attrs: 'maxlength="500"' })}
<div class="at-pi-acts"><button type="button" class="tc-btn sm ghost at-danger" data-pact="del">${esc(t('del_person'))}</button><button type="button" class="tc-btn sm ghost" data-pact="st">${esc(t('st_btn_l'))}</button><button type="button" class="tc-btn sm" data-pact="close">${esc(t('done'))}</button></div></div>`;
}
let preEdit = null;
function peopleChange(e) {
    const el = e.target, li = el.closest('.at-pi');
    if (!li || !el.dataset.k) return;
    const mid = li.dataset.mid, k = el.dataset.k;
    const m = J.members.find((x) => x.id === mid);
    if (!m) return;
    const before = preEdit || snap();
    const p = Object.assign({}, A.person(J, mid));
    if (k === 'from' || k === 'to') m[k] = A.isIso(el.value) ? el.value : '';
    else { if (el.value === '') delete p[k]; else p[k] = el.value; J.people[mid] = A.cleanPerson(p); }
    if (snap() !== before) { push(before); touch(); }
    preEdit = snap();
    if (k === 'name') fu('rename');
    else fu('person_field', { k });
    if (k !== 'name') paintPeople();
    peopleStage();
}
function peopleClick(e) {
    const b = e.target.closest('[data-pact]');
    if (!b) return;
    const li = b.closest('.at-pi'), mid = li && li.dataset.mid;
    if (!mid) return;
    const a = b.dataset.pact;
    if (a === 'open') { ui.pOpen = ui.pOpen === mid ? '' : mid; paintPeople(); return; }
    if (a === 'close') { ui.pOpen = ''; paintPeople(); return; }
    if (a === 'st') { deliverStatement(mid, b); return; }
    if (a === 'del') {
        if (!window.confirm(t('del_q', { who: nameOf(mid) }))) return;
        change(() => A.removeMember(J, mid));
        ui.pOpen = ''; fu('del_person');
    }
}
async function probeOthers() {
    const btn = $('at-from-other');
    if (!btn || !drafts) return;
    try {
        const st = window.__toolDraft.store(SLUG);
        const rows = await st.list();
        btn.hidden = !rows.some((r) => r.meta && r.meta.jid && (!J || r.meta.jid !== J.id) && r.meta.n > 0);
    } catch (_) { btn.hidden = true; }
}
async function fromOther() {
    try {
        const st = window.__toolDraft.store(SLUG);
        const rows = (await st.list()).filter((r) => r.meta && r.meta.jid && r.meta.jid !== J.id && r.meta.n > 0);
        if (!rows.length) { toast(t('other_none'), 'warning'); return; }
        rows.sort((x, y) => ((y.meta.editedAt || y.savedAt || 0) - (x.meta.editedAt || x.savedAt || 0)));
        let pick = rows[0];
        if (rows.length > 1) {
            const lines = rows.slice(0, 9).map((r, i) => (i + 1) + '. ' + (r.label || '?'));
            const ans = window.prompt(t('other_pick') + '\n' + lines.join('\n'), '1');
            if (ans === null) return;
            pick = rows[Math.max(0, Math.min(rows.length - 1, (parseInt(ans, 10) || 1) - 1))];
        }
        const d = await st.load(pick.id);
        const src = d && d.state ? A.sanitize(d.state) : null;
        if (!src) { toast(t('other_none'), 'warning'); return; }
        const recs = src.members.filter((m) => !m.to || m.to >= today()).map((m) => Object.assign({}, A.person(src, m.id), { pays: [] }));
        applyPeople(recs, 'other');
    } catch (err) { if (window.__handleError) window.__handleError(err, 'attendance_from_other', { say: t('imp_failed') }); }
}
function demo() {
    if (J.members.length && !window.confirm(t('demo_q'))) return;
    const names = String(t('demo_names')).split('|').map((s) => s.trim()).filter(Boolean);
    const td = today();
    change(() => {
        const recs = names.map((name, i) => {
            const r = { name };
            if (J.rules.birthdays) r.birth = (+td.slice(0, 4) - (J.preset === 'kindergarten' ? 4 + (i % 3) : 9 + (i % 5))) + '-' + String((+td.slice(5, 7) + i * 5) % 12 + 1).padStart(2, '0') + '-' + String(((i * 7) % 27) + 1).padStart(2, '0');
            return r;
        });
        A.mergePeople(J, recs);
        if (J.when.mode === 'schedule' && !J.when.slots.length) J.when.slots = [A.cleanSlot({ days: [2, 4], t: '17:00' })];
        if (J.when.mode === 'events' && !J.when.extra.length) A.addSession(J, td, { t: '19:00' });
        // прошлые занятия месяца — с отметками, чтобы итоги и выдача были видны сразу
        const list = A.sessions(J, cal, A.addDays(td, -20), A.addDays(td, -1)).filter((s) => !s.cancelled);
        const abs = J.codes.filter((c) => c.kind === 'a');
        const lesson = A.presentCode(J);
        list.forEach((s, k) => {
            // репетитор: у каждого ученика свой день недели; остальные — перекличка всей группы
            if (J.blank === 'none') { J.members.forEach((m, i) => { if (A.isoDow(s.d) === (i % 6) + 1 && lesson) A.setMark(J, s.key, m.id, lesson.id); }); return; }
            if (J.blank !== 'present') A.allPresent(J, s);
            J.members.forEach((m, i) => { if ((i * 5 + k * 3) % 11 === 0 && abs.length) A.setMark(J, s.key, m.id, abs[(i + k) % abs.length].id); });
        });
        if (J.money.mode === 'month' && !J.money.fee) J.money.fee = RU ? 3000 : 300;
        if (J.money.mode === 'visit' && !J.money.price) J.money.price = RU ? 1500 : 40;
        if (J.money.mode === 'pass' && !J.money.passPrice) J.money.passPrice = RU ? 4000 : 120;
        // оплаты: у большинства — внесено, у двоих — нет (видно, как выглядит «к оплате»)
        J.members.forEach((m, i) => {
            if (i % 5 === 3) return;
            const M = J.money;
            if (M.mode === 'month') A.addPay(J, m.id, { d: td.slice(0, 8) + '01', sum: M.fee });
            else if (M.mode === 'pass') A.addPay(J, m.id, { d: A.addDays(td, -21), sum: M.passPrice, n: M.passN });
            else if (M.mode === 'visit') A.addPay(J, m.id, { d: A.addDays(td, -7), sum: M.price * 2 });
        });
        if (!J.title) J.title = t('demo_title_' + J.preset) !== 'demo_title_' + J.preset ? t('demo_title_' + J.preset) : t('demo_title');
    });
    fu('demo', { p: J.preset });
    peopleStage();
    toast(t('demo_done'), 'success');
    setView('roll');
}
async function downloadTemplate() {
    try {
        const ff = await loadFflate();
        const F0 = fields().filter((f) => RU || f.key !== 'account');
        const sheet = templateSheet(F0, { name: String(t('sheet_list')).slice(0, 31) });
        const bytes = window.XlsxWriter.build({ title: t('template_name'), sheets: [sheet] }, ff.zipSync);
        await window.__downloadFile(new Blob([bytes], { type: window.XlsxWriter.MIME }), safeName(t('template_name')) + '.xlsx');
        fu('template');
    } catch (err) { if (window.__handleError) window.__handleError(err, 'attendance_template', { say: t('err_xlsx') }); }
}

/* ── настройки журнала ────────────────────────────────────────────────── */
function weekOrder() {
    const first = conv(LANG).first; // 0 — воскресенье, 1 — понедельник, 6 — суббота
    const start = first === 0 ? 7 : first;
    return Array.from({ length: 7 }, (_, i) => ((start - 1 + i) % 7) + 1);
}
function dayShort(d) { const n = dayNames(LANG); return (n.short || n.long || [])[d % 7] || String(d); }
function paintSettings() {
    const host = $('at-set');
    const open = (k) => (ui.setOpen[k] ? ' open' : '');
    const W = J.when, M = J.money, R = J.rules;
    const modeSeg = ['calendar', 'weekdays', 'schedule', 'events'].map((v) => `<button type="button" class="tc-seg-b${W.mode === v ? ' on' : ''}" data-set="mode" data-v="${v}" aria-pressed="${W.mode === v}">${esc(t('mode_' + v))}</button>`).join('');
    const countries = HS.countries();
    const regions = HS.regionsOf(J.cc || 'NONE');
    const ccSel = `<label class="tc-f"><span class="tc-f-l">${esc(t('f_country'))}</span><select class="tc-in" data-set="cc">${countries.map((c) => `<option value="${esc(c.code)}"${c.code === J.cc ? ' selected' : ''}>${esc(c.code === 'NONE' ? t('no_holidays') : c.label)}</option>`).join('')}</select></label>`
        + (regions.length ? `<label class="tc-f"><span class="tc-f-l">${esc(t('f_region'))}</span><select class="tc-in" data-set="region"><option value="">${esc(t('region_all'))}</option>${regions.map((r) => `<option value="${esc(r.code || r.id)}"${(r.code || r.id) === J.region ? ' selected' : ''}>${esc(r.name || r.label || r.code)}</option>`).join('')}</select></label>` : '');
    const days = weekOrder().map((d) => `<button type="button" class="tc-chip${(W.days || []).indexOf(d) >= 0 ? ' on' : ''}" data-set="day" data-v="${d}" aria-pressed="${(W.days || []).indexOf(d) >= 0}">${esc(dayShort(d))}</button>`).join('');
    const slots = (W.slots || []).map((s, i) => `<div class="at-slot" data-i="${i}"><div class="at-days">${weekOrder().map((d) => `<button type="button" class="tc-chip${s.days.indexOf(d) >= 0 ? ' on' : ''}" data-set="slotday" data-v="${d}" aria-pressed="${s.days.indexOf(d) >= 0}">${esc(dayShort(d))}</button>`).join('')}</div>
<label class="tc-f"><span class="tc-f-l">${esc(t('slot_time'))}</span><input class="tc-in" data-set="slott" type="time" value="${esc(s.t)}"></label>
<label class="tc-f at-slot-lab"><span class="tc-f-l">${esc(t('slot_every'))}</span><select class="tc-in" data-set="slotevery">${[1, 2, 3, 4].map((n) => `<option value="${n}"${s.every === n ? ' selected' : ''}>${esc(n === 1 ? t('every_week') : tn('every_n_weeks', n))}</option>`).join('')}</select></label>
<button type="button" class="at-x" data-set="slotdel" aria-label="${esc(t('slot_del'))}">×</button>
${s.every > 1 ? `<label class="tc-f"><span class="tc-f-l">${esc(t('slot_since'))}</span><input class="tc-in" data-set="slotsince" type="date" value="${esc(s.since || '')}"></label>` : ''}
<label class="tc-f"><span class="tc-f-l">${esc(t('slot_label'))}</span><input class="tc-in" data-set="slotlabel" type="text" maxlength="40" value="${esc(s.label || '')}" placeholder="${esc(t('slot_label_ph'))}"></label></div>`).join('');
    const calls = (W.calls || ['']);
    const callsHtml = `<div class="at-row"><div class="tc-f"><span class="tc-f-l">${esc(t('calls_n'))}</span><div class="tc-seg">${[1, 2, 3].map((n) => `<button type="button" class="tc-seg-b${calls.length === n ? ' on' : ''}" data-set="calls" data-v="${n}" aria-pressed="${calls.length === n}">${n}</button>`).join('')}</div></div>${calls.length > 1 ? calls.map((c, i) => `<label class="tc-f"><span class="tc-f-l">${esc(t('call_label', { n: i + 1 }))}</span><input class="tc-in" data-set="call" data-i="${i}" type="text" maxlength="30" value="${esc(c)}"></label>`).join('') : ''}</div>`;
    const whenBody = `<div class="tc-seg" role="group">${modeSeg}</div><p class="tc-hint">${esc(t('mode_h_' + W.mode))}</p>
${W.mode === 'calendar' ? `<div class="at-row">${ccSel}</div>` : ''}
${W.mode === 'weekdays' ? `<div class="at-days">${days}</div>` : ''}
${W.mode === 'schedule' ? `<div class="at-slots">${slots || `<p class="tc-hint">${esc(t('slots_empty'))}</p>`}</div><div class="tc-actions"><button type="button" class="tc-btn sm ghost" data-set="slotadd">${esc(t('slot_add'))}</button></div>` : ''}
${W.mode === 'calendar' || W.mode === 'weekdays' ? callsHtml : ''}
${W.mode === 'weekdays' || W.mode === 'schedule' ? `<label class="at-chk"><input type="checkbox" data-set="skipHol"${W.skipHol ? ' checked' : ''}> ${esc(t('skip_hol'))}</label>${W.skipHol ? `<div class="at-row">${ccSel}</div>` : ''}` : ''}
<div class="at-row"><label class="tc-f"><span class="tc-f-l">${esc(t('f_start'))}</span><input class="tc-in" type="date" data-set="start" value="${esc(J.start || '')}"></label></div>
<p class="tc-hint">${esc(t('f_start_h'))}</p>`;
    const kinds = [['p', t('kind_p')], ['a', t('kind_a')], ['o', t('kind_o')]];
    const codes = J.codes.map((c, i) => `<div class="at-code" data-i="${i}"><input class="tc-in at-code-k" data-set="ck" type="text" maxlength="4" value="${esc(c.k)}" aria-label="${esc(t('code_k'))}">
<input class="tc-in" data-set="cname" type="text" maxlength="60" value="${esc(c.name)}" aria-label="${esc(t('code_name'))}">
<input class="at-code-col" data-set="ccolor" type="color" value="${esc(c.color)}" aria-label="${esc(t('code_color'))}">
<select class="tc-in at-code-kind" data-set="ckind" aria-label="${esc(t('code_kind'))}">${kinds.map((k) => `<option value="${k[0]}"${c.kind === k[0] ? ' selected' : ''}>${esc(k[1])}</option>`).join('')}</select>
<button type="button" class="at-x" data-set="cdel" aria-label="${esc(t('code_del'))}"${J.codes.length <= 2 ? ' disabled' : ''}>×</button>
<div class="at-code-flags">${c.kind === 'a' ? `<label class="at-chk"><input type="checkbox" data-set="cexc"${c.exc ? ' checked' : ''}> ${esc(t('code_exc'))}</label>` : ''}${c.kind !== 'o' ? `<label class="at-chk"><input type="checkbox" data-set="cpay"${c.pay ? ' checked' : ''}> ${esc(t('code_pay'))}</label>` : ''}${c.kind === 'a' ? `<label class="at-chk"><span>${esc(t('code_ask'))}</span><select class="tc-in" data-set="cask" style="width:auto">${[['', t('ask_none')], ['reason', t('ask_reason')], ['doc', t('ask_doc')]].map((o) => `<option value="${o[0]}"${c.ask === o[0] ? ' selected' : ''}>${esc(o[1])}</option>`).join('')}</select></label>` : ''}${c.kind === 'p' ? `<label class="at-chk"><input type="checkbox" data-set="cpart"${c.part ? ' checked' : ''}> ${esc(t('code_part'))}</label>` : ''}</div></div>`).join('');
    const codesBody = `<div class="at-codes">${codes}</div><div class="tc-actions"><button type="button" class="tc-btn sm ghost" data-set="cadd"${J.codes.length >= 24 ? ' disabled' : ''}>${esc(t('code_add'))}</button></div>
<label class="tc-f"><span class="tc-f-l">${esc(t('blank_l'))}</span><select class="tc-in" data-set="blank">${['present', 'unknown', 'none'].map((v) => `<option value="${v}"${J.blank === v ? ' selected' : ''}>${esc(t('blank_' + v))}</option>`).join('')}</select></label><p class="tc-hint">${esc(t('blank_h'))}</p>`;
    const mSeg = ['none', 'visit', 'pass', 'month'].map((v) => `<button type="button" class="tc-seg-b${M.mode === v ? ' on' : ''}" data-set="money" data-v="${v}" aria-pressed="${M.mode === v}">${esc(t('money_' + v))}</button>`).join('');
    const num = (k, v, lab, attrs) => `<label class="tc-f"><span class="tc-f-l">${esc(lab)}</span><input class="tc-in" data-set="${k}" type="number" min="0" step="${attrs || '0.01'}" inputmode="decimal" value="${esc(v || '')}"></label>`;
    const moneyBody = `<div class="tc-seg" role="group">${mSeg}</div><p class="tc-hint">${esc(t('money_h_' + M.mode))}</p>
${M.mode === 'visit' ? `<div class="at-row">${num('price', M.price, t('f_price'))}</div>` : ''}
${M.mode === 'pass' ? `<div class="at-row">${num('passN', M.passN, t('f_passN'), '1')}${num('passPrice', M.passPrice, t('f_passPrice'))}${num('renewAt', M.renewAt, t('f_renewAt'), '1')}</div>` : ''}
${M.mode === 'month' ? `<div class="at-row">${num('fee', M.fee, t('f_fee'))}</div><label class="at-chk"><input type="checkbox" data-set="recalc"${M.recalc ? ' checked' : ''}> ${esc(t('f_recalc'))}</label>` : ''}
${M.mode !== 'none' ? `<div class="at-row"><label class="tc-f"><span class="tc-f-l">${esc(t('f_cur'))}</span><select class="tc-in" data-set="cur"><option value="">—</option>${CURRENCIES.map((c) => `<option value="${c}"${M.cur === c ? ' selected' : ''}>${c}</option>`).join('')}</select></label></div><p class="tc-hint">${esc(t('money_note'))}</p>` : ''}`;
    const q = R.quorum || { kind: 'pct', v: 0 };
    const rulesBody = `<div class="at-row">${num('streak', R.streak, t('f_streak'), '1')}${num('threshold', R.threshold, t('f_threshold'), '1')}</div>
<div class="at-row"><label class="tc-f"><span class="tc-f-l">${esc(t('f_quorum'))}</span><select class="tc-in" data-set="qkind"><option value="">${esc(t('quorum_off'))}</option><option value="pct"${R.quorum && q.kind === 'pct' ? ' selected' : ''}>${esc(t('quorum_pct'))}</option><option value="n"${R.quorum && q.kind === 'n' ? ' selected' : ''}>${esc(t('quorum_n'))}</option></select></label>${R.quorum ? num('qv', q.v, t('f_quorum_v'), '1') : ''}</div>
<label class="at-chk"><input type="checkbox" data-set="forecast"${R.forecast ? ' checked' : ''}> ${esc(t('f_forecast'))}</label>
<label class="at-chk"><input type="checkbox" data-set="birthdays"${R.birthdays ? ' checked' : ''}> ${esc(t('f_birthdays'))}</label>`;
    const o = J.org || {};
    const txt = (k, v, lab, ph, max) => `<label class="tc-f"><span class="tc-f-l">${esc(lab)}</span><input class="tc-in" data-org="${k}" type="text" maxlength="${max || 120}" value="${esc(v || '')}"${ph ? ` placeholder="${esc(ph)}"` : ''}></label>`;
    const docBody = RU
        ? `<p class="tc-hint">${esc(t('doc_h_ru'))}</p><div class="at-row">${txt('name', o.name, t('org_name'), t('org_name_ph'), 200)}${txt('okpo', o.okpo, t('org_okpo'), '', 10)}</div>
<div class="at-row">${txt('dept', o.dept, t('org_dept'), t('org_dept_ph'))}${txt('calc', o.calc, t('org_calc'), t('org_calc_ph'))}${txt('regime', o.regime, t('org_regime'), t('org_regime_ph'))}</div>
<p class="at-sub-h">${esc(t('org_signs'))}</p><div class="at-row">${txt('head.pos', o.head && o.head.pos, t('org_head_pos'), t('org_head_pos_ph'))}${txt('head.fio', o.head && o.head.fio, t('org_head_fio'), t('fio_ph'), 80)}</div>
<div class="at-row">${txt('resp.pos', o.resp && o.resp.pos, t('org_resp_pos'), t('org_resp_pos_ph'))}${txt('resp.fio', o.resp && o.resp.fio, t('org_resp_fio'), t('fio_ph'), 80)}</div>
<div class="at-row">${txt('teacher', o.teacher, t('org_teacher'), t('fio_ph'), 80)}</div>
<label class="at-chk"><input type="checkbox" data-set="markPresent"${J.form.markPresent ? ' checked' : ''}> ${esc(t('f_mark_present'))}</label>`
        : `<p class="tc-hint">${esc(t('doc_h'))}</p><div class="at-row">${txt('name', o.name, t('org_name_intl'), '', 200)}${txt('teacher', o.teacher, t('org_teacher_intl'), '', 80)}</div>`;
    // камера сканирует ключ имён внутри ящика облака — перерисовка ящиков её бы оборвала
    if (cloud && cloud.state.scan) cloud.stopScan();
    host.innerHTML = [
        drawer('when', '🗓️', t('d_when'), t('d_when_sub'), whenBody, open('when')),
        drawer('codes', '🏷️', t('d_codes'), t('d_codes_sub'), codesBody, open('codes')),
        drawer('money', '💳', t('d_money'), t('d_money_sub'), moneyBody, open('money')),
        drawer('rules', '📏', t('d_rules'), t('d_rules_sub'), rulesBody, open('rules')),
        drawer('doc', '🧾', t('d_doc'), RU ? t('d_doc_sub_ru') : t('d_doc_sub'), docBody, open('doc')),
        // облако и ключ имён: содержимое рисует cloud-ui.js
        drawer('cloud', '☁️', t('d_cloud'), t('d_cloud_sub'), '<div class="at-cloud" id="at-cloud"></div>', open('cloud')),
    ].join('');
    // время слота с готовым значением: change приходит на каждой цифре — сохраняем, когда набор закончен
    if (window.__timeCommit) host.querySelectorAll('[data-set="slott"]').forEach((el) => window.__timeCommit(el, () => setInput({ type: 'change', target: el, timeCommitted: true })));
    if (cloud) cloud.paint();
}
function drawer(k, ico, title, sub, body, open) {
    return `<details class="tc-drawer" id="at-d-${k}" data-keep="${k}"${open}><summary><span class="tc-drawer-ico" aria-hidden="true">${ico}</span>${esc(title)} <span class="tc-drawer-sub">${esc(sub)}</span></summary><div class="tc-drawer-body">${body}</div></details>`;
}
function setClick(e) {
    const el = e.target.closest('[data-set]');
    if (!el || el.tagName === 'INPUT' || el.tagName === 'SELECT') return;
    const k = el.dataset.set, v = el.dataset.v;
    const slot = el.closest('.at-slot'), si = slot ? +slot.dataset.i : -1;
    const code = el.closest('.at-code'), cix = code ? +code.dataset.i : -1;
    change(() => {
        if (k === 'mode') { J.when.mode = v; if (v === 'schedule' && !J.when.slots.length) ui.setOpen.when = true; }
        else if (k === 'day') { const d = +v, a = J.when.days; const i = a.indexOf(d); if (i >= 0) a.splice(i, 1); else a.push(d); a.sort(); }
        else if (k === 'slotday' && si >= 0) { const a = J.when.slots[si].days; const d = +v, i = a.indexOf(d); if (i >= 0 && a.length > 1) a.splice(i, 1); else if (i < 0) a.push(d); a.sort(); }
        else if (k === 'slotdel' && si >= 0) J.when.slots.splice(si, 1);
        else if (k === 'slotadd') J.when.slots.push(A.cleanSlot({ days: [A.isoDow(today())], t: '17:00' }));
        else if (k === 'calls') { const n = +v; const cur = J.when.calls || ['']; const L = I.calls || {}; const def = n === 2 ? [L.morning || '1', L.evening || '2'] : n === 3 ? [L.morning || '1', L.day || '2', L.evening || '3'] : ['']; J.when.calls = n === 1 ? [''] : def.map((x, i) => (cur.length === n && cur[i] ? cur[i] : x)); }
        else if (k === 'money') J.money.mode = v;
        else if (k === 'cdel' && cix >= 0 && J.codes.length > 2) {
            const id = J.codes[cix].id;
            const used = Object.keys(J.marks).some((key) => Object.values(J.marks[key]).indexOf(id) >= 0);
            if (used && !window.confirm(t('code_del_q'))) return;
            J.codes.splice(cix, 1);
            Object.keys(J.marks).forEach((key) => Object.keys(J.marks[key]).forEach((mid) => { if (J.marks[key][mid] === id) A.clearMark(J, key, mid); }));
            if (ui.brush === id) ui.brush = '';
        } else if (k === 'cadd') {
            J.codes.push({ id: A.newCodeId(J), tpl: '', k: '?', name: t('code_new'), color: A.COLORS[J.codes.length % A.COLORS.length], kind: 'a', pay: false, exc: false, ask: '', part: false });
        }
    }, paintSettings);
    fu('setting', { k });
    if (k === 'mode') prepareCal();
}
function setInput(e) {
    const el = e.target;
    const k = el.dataset.set;
    const org = el.dataset.org;
    if (!k && !org) return;
    // правка — по change: один снимок «Отменить» на поле, а не на каждую букву
    if (e.type !== 'change') return;
    if (k === 'slott' && window.__timeCommit && !e.timeCommitted) return;
    const slot = el.closest('.at-slot'), si = slot ? +slot.dataset.i : -1;
    const code = el.closest('.at-code'), ci = code ? +code.dataset.i : -1;
    const v = el.type === 'checkbox' ? el.checked : el.value;
    const num = (x) => { const n = +x; return isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0; };
    let repaint = false;
    const before = snap();
    if (org) {
        const [a, b] = org.split('.');
        if (b) { J.org[a] = Object.assign({}, J.org[a] || {}, { [b]: String(v).slice(0, 120) }); } else J.org[a] = a === 'okpo' ? String(v).replace(/\D/g, '').slice(0, 10) : String(v).slice(0, 200);
    } else if (k === 'cc') { J.cc = v; J.region = HS.defaultRegion(v) || ''; calYears.clear(); prepareCal(); repaint = true; }
    else if (k === 'region') { J.region = v; calYears.clear(); repaint = true; }
    else if (k === 'skipHol') { J.when.skipHol = !!v; repaint = true; }
    else if (k === 'start') { if (A.isIso(v)) J.start = v; }
    else if (k === 'slott' && si >= 0) J.when.slots[si].t = /^\d{1,2}:\d{2}$/.test(v) ? v : '';
    else if (k === 'slotevery' && si >= 0) { const s = J.when.slots[si]; s.every = +v || 1; if (s.every > 1 && !s.since) s.since = nextDow(s.days[0]); repaint = true; }
    else if (k === 'slotsince' && si >= 0) { if (A.isIso(v)) J.when.slots[si].since = v; }
    else if (k === 'slotlabel' && si >= 0) J.when.slots[si].label = String(v).slice(0, 40);
    else if (k === 'call') { const i = +el.dataset.i; J.when.calls[i] = String(v).slice(0, 30); }
    else if (ci >= 0 && /^c/.test(k)) {
        const c = J.codes[ci];
        // код из облака без подписи: человек подписал его сам — ключ имён больше не нужен
        delete c.unknown;
        if (k === 'ck') c.k = String(v).trim().slice(0, 4) || '?';
        else if (k === 'cname') c.name = String(v).slice(0, 60);
        else if (k === 'ccolor') { if (/^#[0-9a-f]{6}$/i.test(v)) c.color = v; codesCss(); }
        else if (k === 'ckind') { c.kind = v; if (v !== 'a') { c.exc = false; c.ask = ''; } if (v !== 'p') c.part = false; repaint = true; }
        else if (k === 'cexc') c.exc = !!v;
        else if (k === 'cpay') c.pay = !!v;
        else if (k === 'cask') c.ask = v;
        else if (k === 'cpart') c.part = !!v;
    } else if (k === 'blank') J.blank = v;
    else if (k === 'price') J.money.price = num(v);
    else if (k === 'fee') J.money.fee = num(v);
    else if (k === 'passN') J.money.passN = Math.max(1, Math.round(num(v)) || 1);
    else if (k === 'passPrice') J.money.passPrice = num(v);
    else if (k === 'renewAt') J.money.renewAt = Math.round(num(v));
    else if (k === 'recalc') J.money.recalc = !!v;
    else if (k === 'cur') J.money.cur = v;
    else if (k === 'streak') J.rules.streak = Math.max(1, Math.round(num(v)) || 1);
    else if (k === 'threshold') J.rules.threshold = Math.min(100, Math.round(num(v)));
    else if (k === 'qkind') { J.rules.quorum = v ? { kind: v, v: v === 'pct' ? 50 : Math.max(1, Math.ceil(J.members.length / 2)) } : null; repaint = true; }
    else if (k === 'qv') { if (J.rules.quorum) J.rules.quorum.v = Math.round(num(v)); }
    else if (k === 'forecast') J.rules.forecast = !!v;
    else if (k === 'birthdays') J.rules.birthdays = !!v;
    else if (k === 'markPresent') J.form.markPresent = !!v;
    if (snap() !== before) { push(before); touch(); fu('setting', { k: org ? 'org' : k }); }
    if (repaint) paintSettings();
    paintFrame();
}
function nextDow(d) { let x = today(); for (let i = 0; i < 7; i++) { if (A.isoDow(x) === d) return x; x = A.addDays(x, 1); } return today(); }

/* ── выдача ───────────────────────────────────────────────────────────── */
async function loadFflate() {
    if (window.fflate && window.fflate.zipSync) return window.fflate;
    await window.__vendorScript(CFG.fflate, { expect: 'fflate', label: 'fflate' });
    return window.fflate;
}
function safeName(s) {
    try { if (window.__outputName && window.__outputName.sanitize) return window.__outputName.sanitize(s) || 'attendance'; } catch (_) {}
    return String(s).replace(/[\\/:*?"<>|;]+/g, ' ').trim() || 'attendance';
}
function baseName(kind) {
    const [y, m] = ui.month;
    const what = kind === 'f608' || kind === 'f608x' ? 'Табель посещаемости 0504608' : kind === 'blank' ? t('file_blank') : t('file_journal');
    return [what, J.title, fmt.month(y, m)].filter(Boolean).join(' — ');
}
function docL() { return I.doc || {}; }
function busy(btn, on) { if (btn) { btn.disabled = !!on; btn.classList.toggle('is-busy', !!on); } }
function pdfFont(serif) { const H = window.HtmlPdf; return H && H.FAMILY ? (serif ? H.FAMILY.serif : H.FAMILY.sans) : (serif ? 'Times New Roman, serif' : 'Arial, sans-serif'); }
async function deliver(kind, btn) {
    if (!J || !J.members.length) { toast(t('err_nobody'), 'warning'); return; }
    const [y, m] = ui.month;
    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, [A.docKey(J, y, m)]) : { commit() {}, abort() {} };
    if (!run) { fu('limit_wall', { kind }); return; }
    busy(btn, true);
    let ok = false;
    const td = today();
    try {
        if (kind === 'xlsx' || kind === 'f608x') {
            const ff = await loadFflate();
            if (!window.XlsxWriter) throw Object.assign(new Error('xlsx_missing'), { say: t('err_xlsx') });
            const spec = kind === 'f608x'
                ? { title: baseName(kind), sheets: [F.f608Sheet(J, cal, y, m, td)] }
                : F.workbook(J, cal, y, m, td, docL(), fmt, { rtl: LANG === 'ar', fields: fields().filter((f) => RU || f.key !== 'account') });
            const bytes = window.XlsxWriter.build(spec, ff.zipSync);
            ok = await window.__downloadFile(new Blob([bytes], { type: window.XlsxWriter.MIME }), safeName(baseName(kind)) + '.xlsx');
        } else {
            if (!window.HtmlPdf) throw Object.assign(new Error('htmlpdf_missing'), { say: t('err_pdf') });
            try { if (window.HtmlPdf.installFonts) window.HtmlPdf.installFonts(); } catch (_) {}
            const host = $('at-paper');
            const res = kind === 'f608'
                ? F.f608Pdf(host, J, cal, y, m, td, { font: pdfFont(true) })
                : F.journalPdf(host, J, cal, y, m, td, docL(), fmt, { blank: kind === 'blank', font: pdfFont(false), rtl: LANG === 'ar', lang: LANG });
            const bytes = await window.HtmlPdf.fromElement(res.root, { pages: 'css', page: 'a4', orientation: 'landscape', title: baseName(kind), lang: kind === 'f608' ? 'ru' : LANG, creator: 'Timbrica', producer: 'Timbrica HtmlPdf' });
            host.innerHTML = '';
            ok = await window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), safeName(baseName(kind)) + '.pdf');
        }
        if (ok) {
            run.commit();
            if (drafts) { try { drafts.exported(); } catch (_) {} }
            stage('export', { kind });
            try { if (window.__aeToolComplete) window.__aeToolComplete({ action: kind, preset: J.preset, people: bucket(J.members.length), sessions: bucket(A.monthSessions(J, cal, y, m).length), money: J.money.mode, when: J.when.mode }); } catch (_) {}
        } else run.abort();
    } catch (err) {
        run.abort();
        try { $('at-paper').innerHTML = ''; } catch (_) {}
        const say = (err && err.say) || (kind === 'xlsx' || kind === 'f608x' ? t('err_xlsx') : t('err_pdf'));
        if (window.__handleError) window.__handleError(err, 'attendance_' + kind, { say });
    } finally { busy(btn, false); }
}
/**
 * Выписка по участнику за период «Итогов» (statement.js): посещения с причинами и справками,
 * начислено и оплачено, расчёт. Работает и без облака. Единица лимита — «журнал × месяц» конца
 * периода, как у выгрузок месяца.
 */
async function deliverStatement(mid, btn) {
    if (!J || !J.members.some((m) => m.id === mid)) return;
    const [a, b] = periodRange();
    const td = today();
    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, [A.docKey(J, +b.slice(0, 4), +b.slice(5, 7))]) : { commit() {}, abort() {} };
    if (!run) { fu('limit_wall', { kind: 'statement' }); return; }
    busy(btn, true);
    try {
        if (!window.HtmlPdf) throw Object.assign(new Error('htmlpdf_missing'), { say: t('err_pdf') });
        try { if (window.HtmlPdf.installFonts) window.HtmlPdf.installFonts(); } catch (_) {}
        const host = $('at-paper');
        const res = statementPdf(host, J, cal, mid, a, b, td, docL(), fmt, { font: pdfFont(false), rtl: LANG === 'ar', lang: LANG });
        const name = [t('st_btn'), A.nameOf(J, mid), fmt.date(a) + ' – ' + fmt.date(b)].filter(Boolean).join(' — ');
        const bytes = await window.HtmlPdf.fromElement(res.root, { pages: 'css', page: 'a4', orientation: 'portrait', title: name, lang: LANG, creator: 'Timbrica', producer: 'Timbrica HtmlPdf' });
        host.innerHTML = '';
        const ok = await window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), safeName(name) + '.pdf');
        if (ok) {
            run.commit();
            stage('export', { kind: 'statement' });
            try { if (window.__aeToolComplete) window.__aeToolComplete({ action: 'statement', preset: J.preset, period: ui.period, pages: res.pages, money: J.money.mode }); } catch (_) {}
        } else run.abort();
    } catch (err) {
        run.abort();
        try { $('at-paper').innerHTML = ''; } catch (_) {}
        if (window.__handleError) window.__handleError(err, 'attendance_statement', { say: (err && err.say) || t('err_pdf') });
    } finally { busy(btn, false); }
}
async function saveFile() {
    if (!J) return;
    try {
        const blob = new Blob([A.toFile(J)], { type: 'application/json' });
        const ok = await window.__downloadFile(blob, safeName(t('file_name') + (J.title ? ' — ' + J.title : '') + ' — ' + today()) + '.json');
        if (ok) fu('file_save', { n: bucket(J.members.length) });
    } catch (err) { if (window.__handleError) window.__handleError(err, 'attendance_file_save', { say: t('file_save_failed') }); }
}
async function openJournalFile(file) {
    let src;
    try {
        if (file.size > 20 * 1048576) throw new A.JournalFileError('broken');
        const text = await file.text();
        // файл «ключа имён» (облако) — не журнал: его вливает cloud-ui в журнал с тем же id
        let o = null;
        try { o = JSON.parse(String(text).replace(/^\uFEFF/, '')); } catch (_) { o = null; }
        if (o && o.kind === KEY_KIND && cloud) { await cloud.takeKey(o, 'file'); return; }
        src = A.fromFile(text);
    } catch (err) {
        const code = err && err.code ? err.code : 'broken';
        toast(t('file_err_' + code) !== 'file_err_' + code ? t('file_err_' + code) : t('file_err_broken'), 'error');
        diag('journal_file_bad', { code });
        return;
    }
    // тот же журнал уже есть на этом устройстве — обновить его, а не завести копию
    try {
        const st = window.__toolDraft && window.__toolDraft.store ? window.__toolDraft.store(SLUG) : null;
        const rows = st ? await st.list() : [];
        const same = rows.find((r) => r.meta && r.meta.jid === src.id);
        if (same && drafts && (!J || J.id !== src.id)) await drafts.open(same.id);
        else if (J && J.id !== src.id && drafts) { try { drafts.leave(); } catch (_) {} }
    } catch (_) {}
    const before = J && J.id === src.id ? snap() : null;
    // файл облачного журнала, открытый поверх него самого: облако остаётся, отличия файла уйдут правками
    if (J && J.id === src.id && J.cloud) src.cloud = J.cloud;
    J = src;
    if (before) push(before); else { hist.past.length = 0; hist.future.length = 0; }
    ui.view = 'roll'; ui.date = today(); ui.sk = ''; ui.open = '';
    calYears.clear(); prepareCal();
    started = true;
    touch();
    paintAll();
    if (cloud) cloud.start();
    fu('file_open', { n: bucket(J.members.length) });
    toast(tn('file_opened', J.members.length), 'success');
}

/* ── «Мои журналы» ────────────────────────────────────────────────────── */
function draftLabel() {
    return [(A.PRESETS[J.preset] || {}).icon || '📋', J.title || presetName(J.preset), J.members.length ? tn('people_n', J.members.length) : '', J.cloud ? '☁' : ''].filter(Boolean).join(' ');
}
function initDrafts() {
    if (!window.__toolDraft || !window.__toolDraft.editor || !$('atDrafts')) return null;
    return window.__toolDraft.editor({
        slug: SLUG, root: 'atDrafts', icon: '📋', openKey: 'at-draft-open',
        // J.cloud едет в черновике: ревизия и неотправленные правки переживают F5 и офлайн
        payload: () => (J ? { v: 1, state: JSON.parse(JSON.stringify(J)), label: draftLabel(), meta: { n: J.members.length, jid: J.id, p: J.preset, cloud: J.cloud ? 1 : 0 } } : null),
        apply: (d) => {
            try { J = A.sanitize(d && d.state); } catch (_) { J = null; }
            hist.past.length = 0; hist.future.length = 0;
            ui.date = today(); ui.sk = ''; ui.open = ''; ui.pOpen = '';
            ui.view = loadSet('view', 'roll') || 'roll';
            calYears.clear(); prepareCal();
            paintAll();
            if (cloud) cloud.start();
            return Promise.resolve([]);
        },
        fresh: () => { J = null; hist.past.length = 0; hist.future.length = 0; started = false; ui.open = ''; paintAll(); if (cloud) cloud.start(); },
        started: () => started && !!J,
    });
}
/** Журнал ведут каждый день: новая вкладка открывает последний (если он не открыт в другой). */
async function openLast() {
    if (J || !drafts) return false;
    try {
        const st = window.__toolDraft.store(SLUG);
        const rows = await st.list();
        if (!rows.length) { try { localStorage.removeItem('at_has_journal'); } catch (_) {} return false; }
        const busyMap = await st.claimedElsewhere(600);
        const pick = rows.filter((r) => !busyMap[r.id]).sort((x, y) => ((y.meta && y.meta.editedAt) || y.savedAt || 0) - ((x.meta && x.meta.editedAt) || x.savedAt || 0))[0];
        if (!pick || J) return false;
        const ok = await drafts.open(pick.id, { prio: 2, busy: busyMap });
        if (ok) fu('journal_reopen', { n: bucket((pick.meta && pick.meta.n) || 0) });
        return ok;
    } catch (_) { return false; }
}

/* ── «Журнал в облаке»: что cloud-ui.js берёт у страницы ─────────────── */
function loginUrl() {
    const u = CFG.loginUrl || ('/' + LANG + '/login');
    return u + (u.indexOf('?') < 0 ? '?' : '&') + 'redirect=' + encodeURIComponent('/' + LANG + '/' + SLUG);
}
async function loadQr() { if (window.qrcode) return; await window.__vendorScript(CFG.qr, { expect: 'qrcode', label: 'qrcode' }); }
/** Сканер QR ключа имён — камера и распознавание грузятся только по нажатию «Сканировать». */
async function loadScan() {
    if (!window.CameraManager) await window.__vendorScript(CFG.camera, { expect: 'CameraManager', label: 'camera-manager' });
    if (!window.__qrDecode) await window.__vendorScript(CFG.qrDecode, { expect: '__qrDecode', label: 'qr-decode' });
}
async function draftRows() { try { return await window.__toolDraft.store(SLUG).list(); } catch (_) { return []; } }
/** Копия журнала в «Моих журналах»: облачная — первой. */
async function findLocal(uid) {
    const rows = (await draftRows()).filter((r) => r.meta && r.meta.jid === uid);
    rows.sort((x, y) => (y.meta.cloud ? 1 : 0) - (x.meta.cloud ? 1 : 0));
    return rows[0] || null;
}
async function openLocal(row) {
    if (J && row.meta && J.id === row.meta.jid && !!J.cloud === !!row.meta.cloud) return true;
    return drafts ? drafts.open(row.id) : false;
}
async function loadLocal(row) {
    try { const d = await window.__toolDraft.store(SLUG).load(row.id); return d && d.state ? A.sanitize(d.state) : null; } catch (_) { return null; }
}
/** Журнал, собранный с сервера (свой с другого устройства, по приглашению): новая работа в «Моих журналах». */
function adopt(j) {
    if (J && drafts) { try { drafts.leave(); } catch (_) {} }
    J = j;
    clearHist();
    ui.view = 'roll'; ui.date = today(); ui.sk = ''; ui.open = ''; ui.pOpen = '';
    calYears.clear(); prepareCal();
    started = true;
    touch();
    paintAll();
}
/** Правка облака (перенос, ключ имён) — не шаг «Отменить»: отмена вернула бы id кодов, которых сервер уже не примет. */
function cloudMutate(fn) { if (!J) return; fn(); clearHist(); touch(); paintAll(); }
function saveQuiet() { if (drafts) { try { drafts.touch(false); } catch (_) {} } }
/* правка коллеги пришла, пока человек набирает в поле, — перерисовка ждёт, пока он из поля выйдет */
let pendingPaint = false;
function typing() {
    const a = document.activeElement;
    return !!(a && $('at') && $('at').contains(a) && (a.tagName === 'TEXTAREA' || (a.tagName === 'INPUT' && /^(text|search|tel|number|date|time)$/.test(a.type || 'text'))));
}
/** Пришли правки с другого устройства: «Отменить» их не откатывает — история начинается заново. */
function remotePaint() { clearHist(); if (typing()) { pendingPaint = true; return; } paintAll(); }
function openSettings() {
    ui.setOpen.cloud = true;
    setView('set');
    const d = $('at-d-cloud');
    if (d) { d.open = true; d.scrollIntoView({ block: 'nearest' }); }
}
function initCloud() {
    return createCloud({
        t, tn, esc, fu, toast, codeLabels, loadFflate, safeName, loadQr, loadScan, loginUrl, openSettings,
        getJ: () => J,
        saveQuiet, paint: remotePaint, mutate: cloudMutate, findLocal, openLocal, loadLocal, adopt,
        locale: LOC, premiumLink: () => CFG.proUrl || '', privacyUrl: () => CFG.privacyUrl || '', fmtDate: (iso) => (A.isIso(iso) ? fmt.date(iso) : iso),
    });
}

/* ── обработчики ──────────────────────────────────────────────────────── */
function wire() {
    $('at-start').addEventListener('click', (e) => { const b = e.target.closest('[data-preset]'); if (b) createJournal(b.dataset.preset); });
    $('at-title').addEventListener('focus', () => { preEdit = snap(); });
    $('at-title').addEventListener('input', (e) => { if (!J) return; J.title = e.target.value.slice(0, 80); touch(); });
    $('at-title').addEventListener('change', () => { if (preEdit && preEdit !== snap()) push(preEdit); preEdit = snap(); fu('title'); });
    $('at-tabs').addEventListener('click', (e) => { const b = e.target.closest('[data-v]'); if (b) setView(b.dataset.v, true); });
    $('at-tabs').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const tabs = [...$('at-tabs').querySelectorAll('[role=tab]')];
        const i = tabs.findIndex((x) => x.dataset.v === ui.view);
        const dir = (e.key === 'ArrowRight') !== (document.documentElement.dir === 'rtl') ? 1 : -1;
        const nx = tabs[(i + dir + tabs.length) % tabs.length];
        e.preventDefault(); setView(nx.dataset.v, true); nx.focus();
    });
    $('at-undo').addEventListener('click', undo);
    $('at-redo').addEventListener('click', redo);
    document.addEventListener('keydown', (e) => {
        if (!J) return;
        const kl = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();
        if (!(e.ctrlKey || e.metaKey) || (kl !== 'z' && kl !== 'y')) return;
        if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
        if (!$('at').contains(document.activeElement) && document.activeElement !== document.body) return;
        e.preventDefault();
        if (kl === 'y' || e.shiftKey) redo(); else undo();
    });
    // перекличка
    $('at-day-prev').addEventListener('click', () => shiftDay(-1));
    $('at-day-next').addEventListener('click', () => shiftDay(1));
    $('at-today').addEventListener('click', () => { ui.date = today(); ui.sk = ''; ui.open = ''; paintAll(); });
    $('at-day-in').addEventListener('change', (e) => { if (A.isIso(e.target.value)) { ui.date = e.target.value; ui.sk = ''; ui.open = ''; fu('day_pick'); paintAll(); } });
    $('at-v-roll').addEventListener('click', rollClick);
    $('at-v-roll').addEventListener('input', rollInput);
    $('at-v-roll').addEventListener('change', rollInput);
    $('at-v-roll').addEventListener('focusin', (e) => { if (e.target.dataset && e.target.dataset.f === 'why') preEdit = snap(); });
    $('at-v-roll').addEventListener('toggle', (e) => { const d = e.target; if (d && d.dataset && d.dataset.keep) ui.setOpen[d.dataset.keep] = d.open; }, true);
    // месяц
    document.querySelectorAll('#at [data-mnav]').forEach((b) => b.addEventListener('click', () => {
        let [y, m] = ui.month;
        const step = ui.view === 'sum' && ui.period === 'quarter' ? 3 : 1;
        m += +b.dataset.mnav * step;
        while (m > 12) { m -= 12; y++; }
        while (m < 1) { m += 12; y--; }
        ui.month = [y, m];
        fu('month_nav', { where: ui.view });
        paintAll();
    }));
    $('at-brush').addEventListener('click', (e) => {
        const r = e.target.closest('#at-range');
        if (r) { ui.range = !ui.range; if (grid) grid.setRange(ui.range); paintBrush(); fu('range_mode', { on: ui.range ? 1 : 0 }); return; }
        const b = e.target.closest('[data-b]');
        if (!b) return;
        ui.brush = b.dataset.b; paintBrush();
        const c = A.code(J, ui.brush);
        $('at-binfo').textContent = c ? c.k + ' — ' + c.name : t('brush_clear');
        fu('brush', { tpl: c ? c.tpl || 'own' : 'clear' });
    });
    // итоги
    $('at-period').addEventListener('click', (e) => { const b = e.target.closest('[data-v]'); if (!b) return; ui.period = b.dataset.v; fu('period', { v: ui.period }); paintSum(); });
    ['at-p-from', 'at-p-to'].forEach((id) => $(id).addEventListener('change', () => { ui.pFrom = $('at-p-from').value; ui.pTo = $('at-p-to').value; paintSum(); }));
    $('at-v-sum').addEventListener('click', (e) => {
        const sb = e.target.closest('[data-st]');
        if (sb) { deliverStatement(sb.dataset.st, sb); return; }
        const f = e.target.closest('[data-filter]');
        if (f) { ui.filter = f.dataset.filter; fu('filter', { v: ui.filter }); paintSum(); return; }
        const p = e.target.closest('[data-pay]');
        if (p) { ui.pay = ui.pay === p.dataset.pay ? '' : p.dataset.pay; paintSum(); const inp = document.querySelector('[data-payf] [name=sum]'); if (inp) inp.focus({ preventScroll: true }); return; }
        const del = e.target.closest('[data-paydel]');
        if (del) { change(() => A.dropPay(J, del.dataset.mid, del.dataset.paydel), paintSum); fu('pay_del'); return; }
        const gt = e.target.closest('[data-goto]');
        if (gt) { ui.date = gt.dataset.goto; ui.sk = gt.dataset.sk || ''; ui.open = ''; setView('roll', true); }
    });
    $('at-v-sum').addEventListener('submit', (e) => {
        const f = e.target.closest('[data-payf]');
        if (!f) return;
        e.preventDefault();
        const mid = f.dataset.payf;
        const d = f.elements.d.value, sum = +f.elements.sum.value, n = f.elements.n ? +f.elements.n.value : 0;
        if (!A.isIso(d) || !(sum >= 0)) { toast(t('pay_bad'), 'warning'); return; }
        change(() => A.addPay(J, mid, { d, sum, n }), paintSum);
        fu('pay_add', { mode: J.money.mode, n: n ? 1 : 0 });
        toast(t('pay_done'), 'success');
    });
    // участники
    $('at-quick').addEventListener('submit', (e) => {
        e.preventDefault();
        const inp = $('at-quick-in');
        const v = inp.value.trim();
        if (!v) return;
        if (/[\n\t;]/.test(v) || v.split(',').length > 2) { importTextBlock(v.replace(/,\s*/g, '\n'), 'quick'); inp.value = ''; return; }
        change(() => { A.addMember(J, { name: v }); });
        inp.value = '';
        fu('add_person', { n: bucket(J.members.length) });
        peopleStage();
        inp.focus({ preventScroll: true });
    });
    $('at-paste-btn').addEventListener('click', () => {
        const box = $('at-paste'), op = box.hidden;
        box.hidden = !op; $('at-paste-btn').setAttribute('aria-expanded', String(op));
        if (op) $('at-paste-ta').focus();
    });
    $('at-paste-cancel').addEventListener('click', () => { $('at-paste').hidden = true; $('at-paste-btn').setAttribute('aria-expanded', 'false'); });
    $('at-paste-go').addEventListener('click', () => {
        const v = $('at-paste-ta').value;
        if (!v.trim()) return;
        importTextBlock(v, 'paste');
        if (J.members.length) { $('at-paste-ta').value = ''; $('at-paste').hidden = true; $('at-paste-btn').setAttribute('aria-expanded', 'false'); }
    });
    $('at-sort').addEventListener('click', () => { change(() => A.sortByName(J, LOC())); fu('sort'); });
    $('at-template').addEventListener('click', downloadTemplate);
    $('at-demo').addEventListener('click', demo);
    $('at-from-other').addEventListener('click', fromOther);
    $('at-p-search').addEventListener('input', (e) => { ui.search = e.target.value; paintPeople(); });
    $('at-plist').addEventListener('focusin', (e) => { if (e.target.dataset && e.target.dataset.k) preEdit = snap(); });
    $('at-plist').addEventListener('change', peopleChange);
    $('at-plist').addEventListener('click', peopleClick);
    // настройки
    $('at-set').addEventListener('click', setClick);
    $('at-set').addEventListener('change', setInput);
    $('at-set').addEventListener('input', setInput);
    $('at-set').addEventListener('focusin', () => { preEdit = snap(); });
    $('at-set').addEventListener('toggle', (e) => { const d = e.target; if (d && d.dataset && d.dataset.keep) ui.setOpen[d.dataset.keep] = d.open; }, true);
    // файлы
    const bindZone = (id, where) => {
        const z = $(id);
        if (!z) return;
        if (window.__bindUploaderFile) window.__bindUploaderFile(z, (f) => onFile(f, where));
        else z.addEventListener('tool-uploader:file', (e) => { const f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0])); if (f) onFile(f, where); });
    };
    bindZone('atImport', 'people');
    bindZone('atOpenFile', 'start');
    // выдача
    $('at-pdf').addEventListener('click', (e) => deliver('pdf', e.currentTarget));
    $('at-xlsx').addEventListener('click', (e) => deliver('xlsx', e.currentTarget));
    $('at-blank').addEventListener('click', (e) => deliver('blank', e.currentTarget));
    if ($('at-f608')) $('at-f608').addEventListener('click', (e) => deliver('f608', e.currentTarget));
    if ($('at-f608x')) $('at-f608x').addEventListener('click', (e) => deliver('f608x', e.currentTarget));
    $('at-save-file').addEventListener('click', saveFile);
    // облако: значок у названия ведёт в ящик «Журнал в облаке»
    if ($('at-cloud-chip')) $('at-cloud-chip').addEventListener('click', openSettings);
    $('at').addEventListener('focusout', () => setTimeout(() => { if (pendingPaint && !typing()) { pendingPaint = false; paintAll(); } }, 0));
}

/* ── старт ────────────────────────────────────────────────────────────── */
function boot() {
    if (!$('at')) return;
    try { HS.configure({ indexUrl: CFG.holidays }); } catch (_) {}
    prepareCal();
    if (window.HtmlPdf && window.HtmlPdf.installFonts) { try { window.HtmlPdf.installFonts(); } catch (_) {} }
    wire();
    cloud = initCloud();
    drafts = initDrafts();
    // нажатие по пресету до скрипта — выполнить
    try { if (window.__earlyAction) window.__earlyAction.claim(SLUG, (a) => { const m = /^preset:(\w+)$/.exec(a || ''); if (m) createJournal(m[1]); }); } catch (_) {}
    const done = () => { $('at').classList.add('is-ready'); stage('ready', { j: J ? 1 : 0, cloud: J && J.cloud ? 1 : 0 }); };
    // ссылка, с которой пришли (#key= / #join=), — после того как открылся журнал устройства
    const cloudBoot = () => cloud.boot().catch((err) => { if (window.__handleError) window.__handleError(err, 'attendance_cloud_boot', { say: t('cloud_err') }); });
    if (drafts) {
        drafts.boot().then(() => (J ? true : openLast())).then(() => { paintAll(); done(); cloudBoot(); }, () => { paintAll(); done(); cloudBoot(); });
    } else { paintAll(); done(); cloudBoot(); }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

export const __test = { fmt, tn };
